import { ReactNode } from 'react'

import { YesNoNaChoice } from '@/models/back-office-pages/annual-review'
import {
  AssignCmpPlanFormValues,
  AuditActions,
  Channel,
  ClientContractFileDTO,
  ClientRelatedCompanyDTO,
  ContractFeeDetailsDTO,
  CreditManagementPlanDTO,
  FactoringSecurityMeasureDTO,
  FactoringSecurityMeasureFormValues,
  InvoiceVerificationFormFields,
  OnboardingFeeType,
  PeriodicalVerificationType,
  RenewalFeeType,
  VerificationType,
} from '@/models/back-office-pages/clients'
import { DUNSSearchType } from '@/models/back-office-pages/duns'
import { LoanBookType } from '@/models/back-office-pages/loan-book'
import {
  AdministrationTypes,
  ClarificationDTO,
  CommentType,
  Currency,
  FromToFilterInput,
  Map_String_DoubleScalar,
  ReportFileDTOInput,
  ReportStatus,
  RepresentativeCompanyDTO,
  RepresentativeDTO,
  SelectOption,
} from '@/models/common'
import { Direction } from '@/models/table'

export enum ProspectTableStatus {
  ALL = 'ALL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  WAITING_APPROVAL = 'WAITING_APPROVAL',
  NEW = 'NEW',
  ARCHIVED = 'ARCHIVED',
  NEEDS_CLARIFICATION = 'NEEDS_CLARIFICATION',
}

export enum ProspectStatus {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  WAITING_APPROVAL = 'WAITING_APPROVAL',
  NEW = 'NEW',
  ARCHIVED = 'ARCHIVED',
  NEEDS_CLARIFICATION = 'NEEDS_CLARIFICATION',
  BLOCKED = 'BLOCKED',
}

export enum DebtorStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  APPROVED_WITH_PROSPECT = 'APPROVED_WITH_PROSPECT',
  REJECTED = 'REJECTED',
  APPROVAL_INITIATED = 'APPROVAL_INITIATED',
  NEW = 'NEW',
  PENDING_WITH_PROSPECT = 'PENDING_WITH_PROSPECT',
  NEEDS_CLARIFICATION = 'NEEDS_CLARIFICATION',
}

export enum EligibilityStatus {
  ELIGIBLE = 'ELIGIBLE',
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
}

export enum TasksTableAssessor {
  EVENT = 'taskEvent',
  DEAL_ID = 'dealId',
  COMPANY_NAME = 'companyName',
  STATUS = 'approvalStatus',
  SALES_ASSIGNEE = 'assignee',
  RISK_ASSIGNEE = 'riskAssignee',
  APPROVED_BY_TASK_RECEIVER = 'approvedByTaskReceiver',
  APPROVAL_LEVEL = 'approvalLevel',
}

export enum FinancialsTableAssessor {
  NAME = 'name',
}

export enum StakeholdersTableAssessor {
  NAME = 'name',
  TYPE = 'type',
}

export enum RiskScoreAssessor {
  NAME = 'name',
  VALUE = 'value',
}

export enum FinancialRatiosAssessor {
  NAME = 'name',
  CURRENT = 'current',
  PREVIOUS = 'previous',
}

export enum RiskReportsAssessor {
  FILE_NAME = 'fileName',
  CREATED_AT = 'reportCreatedAt',
  TYPE = 'reportType',
  UPLOADER = 'uploader',
  DESCRIPTION = 'description',
  ACTIONS = 'actions',
}

export enum SearchDealFieldsNames {
  DEAL_ID = 'dealId',
}

export enum DocumentType {
  SITE_VISIT_REPORT = 'SITE_VISIT_REPORT',
  COMPANY_STRUCTURE = 'COMPANY_STRUCTURE',
  MASTER_AGREEMENT = 'MASTER_AGREEMENT',
  INVOICE_TO_FUND = 'INVOICE_TO_FUND',
}

export enum AddNewKycIdentityFieldsNames {
  NAME = 'name',
  SURNAME = 'surname',
  COUNTRY = 'country',
  ROLE = 'role',
  EMAIL = 'email',
  LANGUAGE = 'communicationCountry',
  BIRTH_YEAR = 'birthYear',
}

export enum PipedriveDealSummaryFieldsNames {
  DEAL_ID = 'dealId',
  DUN_ID = 'dunId',
  REGISTRATION_CODE = 'registrationCode',
  DEAL_NAME = 'dealName',
}

export enum UpdateProspectsDetailsFieldNames {
  STATUS = 'status',
  AML_RISK = 'amlRisk',
  DEAL_ID = 'dealId',
  CURRENCY = 'currency',
  COMPANY_NAME = 'companyName',
  FULL_ADDRESS = 'address',
  STREET_1 = 'companyAddressLine1',
  STREET_2 = 'companyAddressLine2',
  CITY = 'companyAddressCity',
  POSTAL_CODE = 'companyPostalCode',
  COUNTRY = 'companyCountryCode',
  INDUSTRY = 'industry',
  CUSTOM_INDUSTRY = 'industryBySales',
  WEBSITE = 'companyWebPage',
  CONTACT_PERSON_FULL_NAME = 'fullName',
  CONTACT_PERSON_NAME = 'contactPersonName',
  CONTACT_PERSON_SURNAME = 'contactPersonSurname',
  CONTACT_PERSON_PHONE = 'phone',
  CONTACT_PERSON_EMAIL = 'email',
  WORKING_SINCE = 'workingInMarketSince',
  EMPLOYEE_NUMBER = 'employeeNumber',
  LEGAL_FORM = 'legalForm',
  REGISTRATION_CODE = 'companyRegistrationCode',
  VAT = 'vat',
  IBAN = 'clientIban',
  ACCOUNTANT_FULL_NAME = 'accountantFullName',
  ACCOUNTANT_NAME = 'accountantName',
  ACCOUNTANT_SURNAME = 'accountantSurname',
  ACCOUNTANT_PHONE = 'accountantPhone',
  ACCOUNTANT_EMAIL = 'accountantEmail',
  AUTHORIZED_REPRESENTATIVE_FULL_NAME = 'authorizedRepresentativeFullName',
  AUTHORIZED_REPRESENTATIVE_ROLE = 'authorizedRepresentativeRole',
  AUTHORIZED_REPRESENTATIVE_NAME = 'authorizedRepresentativeName',
  AUTHORIZED_REPRESENTATIVE_SURNAME = 'authorizedRepresentativeSurname',
  AUTHORIZED_REPRESENTATIVE_PHONE = 'authorizedRepresentativePhone',
  AUTHORIZED_REPRESENTATIVE_EMAIL = 'authorizedRepresentativeEmail',
}

export enum FinancialLimitsFieldNames {
  REQUESTED_BY_CLIENT = 'askingCreditLimit',
  REQUESTED_BY_SALES = 'calculatedCreditLimit',
  ATRADIUS_PREDICTED = 'atradiusLimitPrediction',
  ATRADIUS = 'atradiusCreditLimit',
  PROPOSED_BY_RISK = 'proposedPricing',
  CREDITSAFE_LIMIT = 'limitFromReport',
  CURRENCY = 'currency',
  INSURANCE_PROVIDER = 'insuranceType',
  DEBTOR_STATEMENT_REQUIRED = 'debtorStatementRequired',
  DEBTOR_STATEMENT_TYPE = 'debtorStatementType',
  CONTRACTED_LIMIT = 'contractedLimit',
}

export enum ProductAndPricingFieldNames {
  COUNTRY = 'companyCountryCode',
  ACTIVE_DEBTOR = 'activeDebtor',
  ADMIN_FEE = 'adminFee',
  APPROVED_DEBTOR = 'approvedDebtor',
  DAILY_FEE = 'dailyFee',
  FACTORING_FEE = 'factoringFee',
  FACTORING_PRODUCT = 'factoringProduct',
  FEE_DAYS_INCLUDED = 'feeDaysIncluded',
  FEE_BASED_ON = 'feesBasedOn',
  FUNDED_RATE = 'fundedRate',
  GRACE_PERIOD = 'gracePeriod',
  INCREASED_FEE = 'increasedFee',
  INSURED_G_RATE = 'insuredGRate',
  INSURED_RATE = 'insuredRate',
  NON_RECOURSE_FEE = 'nonRecourseFee',
  PAYMENT_TERM = 'paymentTerm',
  CREDIT_MANAGEMENT = 'creditManagementType',
  COMMENT = 'creditManagementComment',
  FUNDING_LINE = 'fundingLine',
  PRODUCT_PENALTY = 'productPenalty',
  CONTRACTED_VOLUME = 'contractedVolume',
  ADDITIONAL_PRODUCT = 'additionalProduct',
  ADDITIONAL_PRODUCT_LIMIT = 'additionalProductLimit',
  ADDITIONAL_PRODUCT_COMMENT = 'additionalProductComment',
  CHANNEL = 'channel',
  CLIENT_MANAGER = 'clientManager',
  BROKER_NAME = 'brokerName',
  BROKER_COMMISSION = 'brokerCommission',
  CURRENCY = 'currency',
}

export enum FinancialRatiosFieldNames {
  EBIT = 'ebit',
  QUICK_RATIO = 'quickRatio',
  WORKING_CAPITAL_CYCLE = 'workingCapitalCycle',
  EA_RATIO = 'eaRatio',
  DEBITDA_RATIO = 'debitdaRatio',
  DSO_RATIO = 'dsoRatio',
  PERIOD = 'riskRatiosPeriod',
  SALES_REVENUE = 'netSales',
  NET_PROFIT_LOSS = 'netProfitLoss',
  TURNOVER_PREDICTION = 'turnoverPrediction',
}

export enum UpdateProspectDebtorDetailsFieldNames {
  COMPANY_NAME = 'companyName',
  INDUSTRY = 'industry',
  FULL_ADDRESS = 'address',
  COUNTRY_CODE = 'countryCode',
  ADDRESS = 'companyAddress',
  WEBSITE = 'companyWebPage',
  WORKING_SINCE = 'workingInMarketSince',
  REGISTRATION_CODE = 'companyRegistrationCode',
  DUN_ID = 'dunId',
  VAT = 'vat',
  PAYMENT_TERM = 'paymentTerm',
  MONTHLY_TURNOVER = 'expectedFactoringVolume',
  FULL_NAME = 'fullName',
  NAME = 'contactPersonFirstName',
  SURNAME = 'contactPersonLastName',
  PHONE = 'phone',
  EMAIL = 'email',
  LEGAL_FORM = 'legalForm',
}

export enum BalanceSheetFieldNames {
  AAOY_AMOUNTS_PAYABLE_TO_ASSOCIATED_ENTITIES = 'aaoyAmountsPayableToAssociatedEntities',
  AAOY_AMOUNTS_PAYABLE_TO_ENTITIES = 'aaoyAmountsPayableToEntities',
  AAOY_OTHER = 'aaoyOtherAmounts',
  AWOY_AMOUNTS_PAYABLE_TO_ASSOCIATED_ENTITIES = 'awoyAmountsPayableToAssociatedEntities',
  AWOY_AMOUNTS_PAYABLE_TO_ENTITIES = 'awoyAmountsPayableToEntities',
  AWOY_LIABILITIES_OF_TAX_ON_PROFIT = 'awoyLiabilitiesOfTaxOnProfit',
  AWOY_LIABILITIES_RELATED_TO_EMPLOYMENT_ENTITIES = 'awoyLiabilitiesRelatedToEmploymentEntities',
  AWOY_OTHER = 'awoyOtherAmounts',
  SHARED_PREMIUM_ACCOUNT = 'sharePremiumAccount',
  AAOY_AMOUNTS_OWED_TO_CREDIT_INSTITUTIONS = 'aaoyAmountsOwedToCreditInstitutions',
  AAOY_AMOUNTS_PAYABLE_UNDER_BILLS = 'aaoyAmountsPayableUnderBills',
  AAOY_DEBENTURE_LOANS = 'aaoyDebentureLoans',
  AAOY_PAYMENTS_RECEIVED = 'aaoyPaymentsReceived',
  AAOY_TRADE_CREDITORS = 'aaoyTradeCreditors',
  AWOY_AMOUNTS_OWED_TO_CREDIT_INSTITUTIONS = 'awoyAmountsOwedToCreditInstitutions',
  AWOY_AMOUNTS_PAYABLE_UNDER_BILLS = 'awoyAmountsPayableUnderBills',
  AWOY_DEBENTURE_LOANS = 'awoyDebentureLoans',
  AWOY_PAYMENTS_RECEIVED = 'awoyPaymentsReceived',
  AWOY_TRADE_CREDITORS = 'awoyTradeCreditors',
  CAPITAL = 'capital',
  CASH = 'cash',
  FINANCIALS = 'financials',
  FIXED_ASSETS_OTHERS = 'fixedAssetsOthers',
  GRANTS_AND_SUBSADIES = 'grantsAndSubsidies',
  INCOMES = 'incomes',
  INTANGIBLES = 'intangibles',
  ACCRUALS = 'accruals',
  PROVISIONS = 'provisions',
  RECEIVABLES_WITHIN_YEAR = 'receivablesWithinYear',
  RESERVES = 'reserves',
  RETAINED_PROFIT_LOSS = 'retainedProfitLoss',
  REVALUATION_RESERVE = 'revaluationReserve',
  SHORT_TERM_INVESTMENTS = 'shortTermInvestments',
  STOCKS = 'stocks',
  TANGIBLES = 'tangibles',
  OTHER_SHORT_TERM_ASSETS = 'otherShortTermAssets',
}

export enum ProfitLossFieldNames {
  ADJUSTMENTS_OF_REDUCTION = 'adjustmentsOfReduction',
  ADMINISTRATIVE_COSTS = 'administrativeCosts',
  BALANCE_CHANGES_REVENUE = 'balanceChangesRevenue',
  DISTRIBUTION_COSTS = 'distributionCosts',
  ECONOMIC_ACTIVITY_REVENUE = 'economicActivityRevenue',
  FINANCIAL_YEAR_TAX = 'financialYearTax',
  GROSS_PROFIT_LOSS = 'grossProfitLoss',
  INTEREST_PAYMENTS = 'interestPayments',
  INTEREST_REVENUE = 'interestRevenue',
  NET_TURNOVER = 'netTurnover',
  OPERATING_CHANGES = 'operatingCharges',
  PARTICIPATION_REVENUE = 'participationRevenue',
  PRODUCTION_COST = 'productionCost',
  PROFIT_LOSS_AFTER_TAX = 'profitLossAfterTax',
  PROFIT_LOSS_BEFORE_TAX = 'profitLossBeforeTax',
  PROFIT_LOSS_FINANCIAL_YEAR = 'profitLossFinancialYear',
  SECURITIES_REVENUE = 'securitiesRevenue',
  OPERATING_EXPENSES = 'operatingExpenses',
  SALES_EXPENSES = 'salesExpenses',
  OTHER_ACTIVITIES = 'otherActivities',
  OPERATING_PROFIT = 'operatingProfit',
  GAINS = 'gains',
  EXCLUSIONS = 'exclusions',
  MINORITY_INTEREST = 'minorityInterest',
  NET_PROFIT = 'netProfit',
  OTHER_REVENUE = 'otherRevenue',
  INVENTORIES_AND_RESOURCES = 'inventoriesAndResources',
}

export enum AmortizationFieldNames {
  AMORTIZATION = 'amortization',
}

export enum ProspectApprovalFieldNames {
  COMMENT = 'comment',
  APPROVAL_LEVEL = 'approvalLevel',
  REJECT_REASON = 'rejectReason',
  DEBTORS_FOR_APPROVAL = 'debtorsForApproval',
  ARCHIVE_REASON = 'archiveReason',
  SOURCING_AGENT_ID = 'sourcingAgentId',
  CLARIFICATION_FOR_ACCOUNTANT = 'clarificationForAccountant',
  DEBTORS_IDS = 'debtorsIds',
}

export enum CreateFabClientFieldNames {
  DEBTORS_IDS = 'debtorsIds',
  ONBOARDING_FEE_SETTLEMENT = 'onboardingFeeSettlement',
  ADD_ONBOARDING_FEE_SETTLEMENT = 'addOnboardingFeeSettlement',
  CREDIT_LIMIT_FEE_SETTLEMENT = 'creditLimitFeeSettlement',
  ADD_CREDIT_LIMIT_FEE_SETTLEMENT = 'addCreditLimitFeeSettlement',
}

export enum CloseApprovalFieldNames {
  COMMENT = 'comment',
  CLOSE_REASON = 'closeReason',
}

export enum SummaryFieldNames {
  SUMMARY = 'summary',
  MAX_CHARACTERS = 'maxCharacters',
}

export enum AssigneesFieldNames {
  RISK = 'riskAssignee',
  SALES = 'salesAssignee',
}

export enum RiskReportFieldNames {
  TYPE = 'reportType',
  FILES = 'files',
  SEND_REPORT_TO_RECEIVERS = 'sendReportToReceivers',
  CREATED_AT = 'reportCreatedAt',
  DESCRIPTION = 'description',
}

export enum SalesQuestionnaireFieldNames {
  WORKING_CAPITAL = 'workingCapital',
  DEBT_REPAYMENT = 'debtRepayment',
  REFINANCING = 'refinancing',
  PURCHASE_OF_ASSETS = 'purchaseOfAssets',
  BUSINESS_EXPANSION = 'businessOfExpansion',
  PURPOSE_OF_FUNDING_OTHER = 'purposeOfFundingOther',
  PURPOSE_OF_FUNDING_COMMENT = 'purposeOfFundingComments',
  G_ACCOUNT = 'accountUsed',
  CLIENT_ACTIVITY_SECTOR = 'clientActivitySector',
  CREDIT_FACILITY_WITH_BANK = 'creditFacilityWithBank',
  CREDIT_FACILITY_WITH_BANK_CHOICE = 'creditFacilityWithBankChoice',
  CREDIT_FACILITY_WITH_BANK_COMMENTS = 'creditFacilityWithBankComments',
  CREDITORS = 'creditors',
  OUTSTANDING_RECEIVABLES_OVERDUE = 'outstandingReceivablesOverdue',
  OUTSTANDING_RECEIVABLES_OVERDUE_CHOICE = 'outstandingReceivablesOverdueChoice',
  OUTSTANDING_RECEIVABLES_OVERDUE_COMMENT = 'outstandingReceivablesOverdueComments',
  OUTSTANDING_RECEIVABLES_OVERDUE_AMOUNT = 'outstandingReceivablesOverdueAmount',
  CLIENT_INVOLVED_IN_LAWSUIT = 'clientInvolvedInLawsuit',
  CLIENT_INVOLVED_IN_LAWSUIT_COMMENT = 'clientInvolvedInLawsuitComments',
  CLIENT_WITH_PERSONAL_GUARANTEE = 'clientWillingGiveGuarantee',
  CLIENT_WITH_PERSONAL_GUARANTEE_COMMENT = 'clientWillingGiveGuaranteeComments',
  HAVE_UNPAID_LIABILITIES = 'havingUnpaidLiabilities',
  HAVE_UNPAID_LIABILITIES_COMMENT = 'havingUnpaidLiabilitiesComments',
  PERMISSION_TO_CONTACT_ACCOUNTANT = 'permissionToContactAccountant',
  PCA_NAME = 'pcaName',
  PCA_SURNAME = 'pcaSurname',
  PCA_PHONE = 'pcaPhone',
  PCA_EMAIL = 'pcaEmail',
  CLIENT_VISIT = 'clientVisit',
  VISIT_DATE = 'visitDate',
  VISITED_PERSON = 'personWhoVisited',
  VISITED_PLACE = 'visitedPlaceAddress',
  VISIT_COMMENT = 'clientVisitComments',
  ID_SEEN = 'personalIdSeen',
  COMPANY_DIRECTOR = 'companyDirector',
  COMPANIES = 'companyDirectors',
  COMPANY_BELONGS_TO_GROUP = 'companyBelongsToGroup',
  ORGANIZATION_STRUCTURE_ATTACHED = 'organizationStructureAttached',
  NUMBER_OF_DEBTORS = 'numberOfDebtor',
  CREDITOR = 'creditor',
  PLEDGE = 'pledge',
  AMOUNT = 'amount',
  AMOUNT_OF_SHARES = 'amountOfShares',
  COMPANY_DETAILS = 'companyDetails',
  LEAD_SOURCE = 'leadSource',
}

export enum SalesSummaryQuestionnaireFieldNames {
  ADDITIONAL_INFORMATION = 'additionalInformation',
  AVERAGE_AMOUNT_PER_INVOICE = 'averageAmountPerInvoice',
  CONTRACT_IN_PLACE = 'contractInPlace',
  CONTRACT_IN_PLACE_COMMENT = 'contractInPlaceComment',
  DAYS_SALES_OUTSTANDING = 'daysSalesOutstanding',
  DEBTOR_STATEMENT_AGREED_TO_BE_SIGNED = 'debtorStatementAgreedToBeSigned',
  DEBTOR_STATEMENT_AGREED_TO_BE_SIGNED_COMMENT = 'debtorStatementAgreedToBeSignedComment',
  EXPECTED_AVERAGE_AMOUNT_PER_INVOICE = 'expectedAverageAmountPerInvoice',
  EXPECTED_MONTHLY_TURNOVER = 'expectedMonthlyTurnover',
  EXPECTED_PAYMENT_TERM = 'expectedPaymentTerm',
  HOW_OFTEN_SELLER_INVOICE = 'howOftenSellerInvoice',
  INVOICING_PROCESS = 'invoicingProcess',
  LENGTH_OF_TRADING_EXPERIENCE = 'lengthOfTradingExperience',
  MONTHLY_TURNOVER = 'monthlyTurnover',
  OVERDUE = 'overdue',
  PAYMENT_EXPERIENCE = 'paymentExperience',
  PAYMENT_TERM = 'paymentTerm',
  PROSPECT_ID = 'prospectId',
  SHORT_DESCRIPTION = 'shortDescription',
  SUPPORTING_DOCUMENTS = 'supportingDocuments',
}

export enum RiskQuestionnaireFieldNames {
  HIT_IN_ELIGIBILITY = 'hitInEligibility',
  REVENUE_LESS_50K = 'revenue',
  WORKING_IN_MARKET = 'workingInMarket',
  LEGAL_STATUS = 'legalStatus',
  HEADQUARTERS_NOT_IN_EU = 'headquartersNotInEurope',
  HIT_FOR_MANDATORY_GUARANTEE = 'hitForMandatoryGuarantee',
  LIMIT_MORE_100K = 'limitGt100KAndEquityLs50Proc',
  NEGATIVE_EQUITY = 'negativeEquity',
  UNPROFITABLE_ACTIVITY = 'unprofitableActivity',
  TRADING_HISTORY = 'tradingHistory',
  HAS_ACTIVE_LAWSUIT = 'clientHasActiveLawsuit',
  HAS_DELAY_IN_TAXES = 'clientHasDelaysInTaxes',
  FORBIDDEN_INDUSTRY = 'changeToForbiddenIndustry',
  CHANGES_IN_6_MONTHS = 'changesInSixMonths',
  CHANGES_IN_6_MONTHS_COMMENT = 'changesInSixMonthsComments',
  HIT_IN_STOP_LIST = 'hitInternalStopList',
  HIT_IN_STOP_LIST_COMMENT = 'hitInternalStopListComments',
  HIT_IN_BANKRUPTCY = 'hitOngoingBankruptcy',
  HIT_IN_BANKRUPTCY_COMMENT = 'hitOngoingBankruptcyComments',
  HIT_IN_KYC = 'hitInKyc',
  HIT_IN_KYC_COMMENT = 'hitInKycComments',
  CONCENTRATION_LIMIT_EXCEEDED = 'concentrationLimitExceeded',
  CONCENTRATION_LIMIT_EXCEEDED_COMMENT = 'concentrationLimitExceededComments',
  ACCOUNT_RECEIVABLES_PLEDGED = 'accountReceivablesPledged',
  ACCOUNT_RECEIVABLES_PLEDGED_COMMENT = 'accountReceivablesPledgedComments',
  WED_DOMAIN_CHECK = 'webDomainCheck',
  WED_DOMAIN_CHECK_COMMENT = 'webDomainCheckComments',
  SITE_VISIT_REPORT_CHECK = 'siteVisitReportCheck',
  SITE_VISIT_REPORT_CHECK_COMMENT = 'siteVisitReportCheckComments',
  CALL_WITH_ACCOUNTANT = 'callWithAccountant',
  ADDRESS = 'accountantOfficeAddress',
  HOW_LONG_WORK_WITH = 'companyWorkWithThisAccountant',
  DEBTORS_PLEDGE = 'debtorsPledge',
  TAX_DEBTS = 'taxDebts',
  PROPERTY_OWNS_RENT = 'propertyOwnsOrRents',
  IRREGULARITIES = 'irregularities',
  DATE = 'dateOfAccountantSpoken',
  DURATION = 'conversationDuration',
  COMPANY_FOUND = 'prospectCompanyFound',
  COMPANY_FOUND_COMMENT = 'prospectCompanyFoundComments',
  REPRESENTATIVE_FOUND = 'prospectRepresentativeFound',
  REPRESENTATIVE_FOUND_COMMENT = 'prospectRepresentativeFoundComments',
}

export interface ProspectDTO {
  applicationDate: string
  approvalRequestedOn: string
  lastApprovalDecisionOn: string
  approvalLevel?: ApprovalLevel
  approversCount: number
  approversTotalCount: number
  companyName: string
  completeness: number
  countryCode: string
  dealId: number
  dunId: string
  eligibilityStatus: EligibilityStatus
  email: string
  firstName: string
  id: string
  kycRisk: KycRisk
  lastName: string
  phone: string
  registrationCode: string
  riskAssignee: string
  salesAssignee: string
  status: ProspectStatus
  stoplistMatch: string

  [key: string]: string | ReactNode
}

export interface Page_ProspectDTO {
  content: Array<ProspectDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ProspectsData {
  prospects: Page_ProspectDTO
}

export interface ProspectsVariables {
  companyName?: string
  applicationDate?: FromToFilterInput
  approvalRequestedOn?: FromToFilterInput
  lastApprovalDecisionOn?: FromToFilterInput
  pageSize?: number
  pageNum?: number
  tableFilteringStatus: ProspectTableStatus
  sortDirection?: Direction
  sortField?: string
}

export interface ProspectListReportResponseDTO {
  reportId: string
}

export interface RequestGenerateProspectsReportData {
  prospectListReportRequest: ProspectListReportResponseDTO
}

export interface RequestGenerateProspectsReportVariables {
  dunId?: string
  dealId?: number
  companyName?: string
  kycRisk?: Array<KycRisk>
  tableFilteringStatus: ProspectTableStatus
  stoplistMatch?: boolean
  riskAssignee?: string
  countryCode?: [string]
  salesAssignee?: string
  statuses?: Array<ProspectStatus>
  registrationCode?: string
  approvalLevel?: Array<ApprovalLevel>
  applicationDate?: FromToFilterInput
}

export interface ProspectListReportFileDTO {
  documentId: string
  fileName: string
  createdOn: Date
  status: ReportStatus
  uploader: string
  content: string
}

interface Page_ProspectListReportFileDTO {
  content: ProspectListReportFileDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ProspectsReportsData {
  prospectListReportsList: Page_ProspectListReportFileDTO
}

export interface ProspectsReportsVariables {
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
  createdOn?: FromToFilterInput
}

export interface CancelProspectsReportData {
  cancelProspectListReport: ProspectListReportResponseDTO
}

export interface CancelProspectsReportVariables {
  reportId: string
}

export interface DownloadProspectsReportData {
  downloadProspectListReport: ProspectListReportFileDTO
}

export interface DownloadProspectsReportVariables {
  documentId: string
}

export enum StakeholderType {
  LEGAL_REPRESENTATIVE = 'LEGAL_REPRESENTATIVE',
  UBO = 'UBO',
}

export enum FactoringModel {
  AMERICAN_FACTORING = 'AMERICAN_FACTORING',
  INVOICE_FINANCING = 'INVOICE_FINANCING',
  INVOICE_DISCOUNTING = 'INVOICE_DISCOUNTING',
}

export enum FundingLine {
  AEGON = 'AEGON',
  BND = 'BND',
  SPV = 'SPV',
  INVEGA = 'INVEGA',
  AGC = 'AGC',
  ATB = 'ATB',
  DEXTERITAS = 'DEXTERITAS',
}

export enum Recourseness {
  RECOURSE = 'RECOURSE',
  NON_RECOURSE = 'NON_RECOURSE',
  MIXED = 'MIXED',
}

export enum BusinessGuaranteesAgency {
  INVEGA = 'INVEGA',
  SILENT = 'SILENT',
}

export enum FactoringProductType {
  AMERICAN_FACTORING_RECOURSE = 'AMERICAN_FACTORING_RECOURSE',
  AMERICAN_FACTORING_NON_RECOURSE = 'AMERICAN_FACTORING_NON_RECOURSE',
  INVOICE_FINANCING_RECOURSE = 'INVOICE_FINANCING_RECOURSE',
  INVOICE_FINANCING_NON_RECOURSE = 'INVOICE_FINANCING_NON_RECOURSE',
  INVOICE_DISCOUNTING_NON_RECOURSE = 'INVOICE_DISCOUNTING_NON_RECOURSE',
  INVOICE_FINANCING_RECOURSE_INVEGA = 'INVOICE_FINANCING_RECOURSE_INVEGA',
  INVOICE_FINANCING_NON_RECOURSE_INVEGA = 'INVOICE_FINANCING_NON_RECOURSE_INVEGA',
  INVOICE_FINANCING_SILENT = 'INVOICE_FINANCING_SILENT',
  LOANS = 'LOANS',
  CREDIT_LINE = 'CREDIT_LINE',
  REVERSE_FACTORING = 'REVERSE_FACTORING',
  N_A = 'N_A',
  INVOICE_FINANCING_MIXED = 'INVOICE_FINANCING_MIXED',
  AMERICAN_FACTORING_MIXED = 'AMERICAN_FACTORING_MIXED',
  AMERICAN_FACTORING_RECOURSE_SILENT = 'AMERICAN_FACTORING_RECOURSE_SILENT',
  INVOICE_FINANCING_MIXED_SILENT = 'INVOICE_FINANCING_MIXED_SILENT',
  INVOICE_FINANCING_NON_RECOURSE_SILENT = 'INVOICE_FINANCING_NON_RECOURSE_SILENT',
}

export enum CreditManagementType {
  CUSTOM = 'CUSTOM',
  STANDARD = 'STANDARD',
}

export enum FeesType {
  FACTOR_AMOUNT = 'FACTOR_AMOUNT',
  OUTSTANDING_AMOUNT = 'OUTSTANDING_AMOUNT',
}

export enum AtradiusLimit {
  NOT_GIVEN = 'NOT_GIVEN',
  BETWEEN_0_AND_30K = 'BETWEEN_0_AND_30K',
  BETWEEN_30K_AND_100K = 'BETWEEN_30K_AND_100K',
  GREATER_THAN_100K = 'GREATER_THAN_100K',
}

export enum ApprovalStatus {
  APPROVED = 'APPROVED',
  ARCHIVED = 'ARCHIVED',
  NEW = 'NEW',
  PENDING_RISK = 'PENDING_RISK',
  REJECTED = 'REJECTED',
  PENDING_WITH_PROSPECT = 'PENDING_WITH_PROSPECT',
  APPROVED_WITH_PROSPECT = 'APPROVED_WITH_PROSPECT',
  NEEDS_CLARIFICATION = 'NEEDS_CLARIFICATION',
}

export enum ApprovalDecision {
  APPROVE = 'APPROVE',
  INITIATED = 'INITIATED',
  NEW = 'NEW',
  REJECT = 'REJECT',
}

export enum ApprovalRejectedReason {
  CLIENT_DEBTOR_WILL_NOT_SIGN = 'CLIENT_DEBTOR_WILL_NOT_SIGN',
  CLIENT_INFO_DOCUMENTS_MISSING = 'CLIENT_INFO_DOCUMENTS_MISSING',
  CLIENT_INSURANCE_LIMIT = 'CLIENT_INSURANCE_LIMIT',
  CLIENT_NEEDS_MORE_FUNDING_THAN_OFFERED = 'CLIENT_NEEDS_MORE_FUNDING_THAN_OFFERED',
  CLIENT_PAID_BY_DEBTOR = 'CLIENT_PAID_BY_DEBTOR',
  COMPETITION_SIGNED_WITH_COMPETITOR = 'COMPETITION_SIGNED_WITH_COMPETITOR',
  DD_LOST_TO_PLEDGE = 'DD_LOST_TO_PLEDGE',
  DD_REJECTED_BY_RISK = 'DD_REJECTED_BY_RISK',
  DELAYS_NON_RESPONSIVE_CLIENT = 'DELAYS_NON_RESPONSIVE_CLIENT',
  NO_FIT_CONDITIONS = 'NO_FIT_CONDITIONS',
  ABSENCE_OF_COLLATERAL = 'ABSENCE_OF_COLLATERAL',
  ACTIVE_PAYMENT_DELAYS = 'ACTIVE_PAYMENT_DELAYS',
  BAD_HISTORY_OR_FRAUD = 'BAD_HISTORY_OR_FRAUD',
  NOT_ELIGIBLE_BUSINESS_SECTOR = 'NOT_ELIGIBLE_BUSINESS_SECTOR',
  CLIENT_FROM_FOREIGN_COUNTRY = 'CLIENT_FROM_FOREIGN_COUNTRY',
  COMPETITOR = 'COMPETITOR',
  DEAL_STRUCTURE = 'DEAL_STRUCTURE',
  DEBTOR_IS_OUTSIDE_OECD = 'DEBTOR_IS_OUTSIDE_OECD',
  DEBTOR_WONT_SIGN = 'DEBTOR_WONT_SIGN',
  EXCESSIVE_COLLATERAL_REQUIREMENTS = 'EXCESSIVE_COLLATERAL_REQUIREMENTS',
  FACTORING_DOESNT_FIT = 'FACTORING_DOESNT_FIT',
  FINANCIALLY_UNSTABLE = 'FINANCIALLY_UNSTABLE',
  GHOSTED = 'GHOSTED',
  INDIVIDUAL = 'INDIVIDUAL',
  INSURANCE_LIMIT_NOT_RECEIVED_CWI = 'INSURANCE_LIMIT_NOT_RECEIVED_CWI',
  INSURANCE_LIMIT_NOT_RECEIVED_LTS = 'INSURANCE_LIMIT_NOT_RECEIVED_LTS',
  MINIMUM_CRITERIA = 'MINIMUM_CRITERIA',
  NEEDED_LOAN_MINIMUM_CRITERIA = 'NEEDED_LOAN_MINIMUM_CRITERIA',
  NEEDS_RESOLVED = 'NEEDS_RESOLVED',
  ONE_TIME_INVOICE = 'ONE_TIME_INVOICE',
  PRICING = 'PRICING',
  RECEIVABLES_PLEDGED = 'RECEIVABLES_PLEDGED',
  SHOPPING = 'SHOPPING',
  TOO_MUCH_DOCUMENTATION_REQUEST = 'TOO_MUCH_DOCUMENTATION_REQUEST',
  CLIENT_AND_DEBTOR_INTERCONNECTED = 'CLIENT_AND_DEBTOR_INTERCONNECTED',
}

export enum ArchiveReason {
  CREATED_IN_FAB_MANUALLY = 'CREATED_IN_FAB_MANUALLY',
  REJECTED_BY_CLIENT = 'REJECTED_BY_CLIENT',
  REJECTED_BY_FACTRIS = 'REJECTED_BY_FACTRIS',
  REJECTED_BY_INSURANCE = 'REJECTED_BY_INSURANCE',
}

export enum ApprovalCloseReason {
  CLIENT_COMPETITOR = 'CLIENT_COMPETITOR',
  CLIENT_DEBTOR_WILL_NOT_SIGN = 'CLIENT_DEBTOR_WILL_NOT_SIGN',
  CLIENT_MISSING_DOCUMENTS = 'CLIENT_MISSING_DOCUMENTS',
  CLIENT_NEEDS_RESOLVED = 'CLIENT_NEEDS_RESOLVED',
  CLIENT_NON_RESPONSIVE = 'CLIENT_NON_RESPONSIVE',
  CLIENT_PRICING = 'CLIENT_PRICING',
  CLIENT_SHOPPING = 'CLIENT_SHOPPING',
  NO_FIT_CONDITIONS = 'NO_FIT_CONDITIONS',
}

export interface PipedriveDealDTO {
  dealId: string
  dealName?: string
  dunId: string
  registrationCode: string
  countryCode: string
}

export interface SearchDealData {
  findPipedriveDeal: PipedriveDealDTO
}

export interface SearchDealTestingData {
  findPipedriveDealTesting: PipedriveDealDTO
}

export interface SearchDealVariables {
  dealId: string
}

export interface AddNewProspectData {
  exportProspectFromPipedrive: ProspectDTO
}

export interface AddNewProspectTestingData {
  exportProspectFromPipedriveTesting: ProspectDTO
}

export interface AddNewProspectVariables {
  dealId: string
  dunId: string
}

export interface AddNewProspectFormValues {
  registrationCode: string
  dunId: string
  searchType: DUNSSearchType
  companyCountryCode: string
}

export interface DunCompany {
  address: string
  country: string
  dunId: string
  name: string
  searchTerm: string
  industry: string
  vat: string
  registrationCode: string
  legalForm: CompanyLegalForm
  postalCode: string
  city: string
  street: string
}

export interface SearchDunCompanyVariables {
  searchTerm: string
  countryCode?: string
}

export interface SearchDunCompanyData {
  searchDunsCompanies: Array<DunCompany>
}

export interface AddNewProspectFormFields {
  dealId: string
  dunId: string
  searchType: DUNSSearchType
  companyCountryCode: string
}

export interface SearchDunCompanyByIdVariables {
  dunId: string
}

export interface CompanyFileDTOInput {
  content?: string
  fileName: string
  id?: string
  size?: number
  documentType?: DocumentType
}

export enum CompanyLegalForm {
  COMPANY = 'COMPANY',
  PARTNERSHIP = 'PARTNERSHIP',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  OTHER = 'OTHER',
}

export interface ProspectSummaryDTO {
  dealId: number
  industry: string
  industryBySales: string
  companyName: string
  companyAddressCity: string
  companyAddressLine1: string
  companyAddressLine2: string
  companyPostalCode: string
  companyCountryCode: string
  companyWebPage: string
  legalForm: CompanyLegalForm
  workingInMarketSince: string
  employeeNumber: number | string
  // Identification numbers
  companyRegistrationCode: string
  vat: string
  // Contact person
  contactPersonName: string
  contactPersonSurname: string
  phone: string
  email: string
  // Accountant
  accountantEmail: string
  accountantName: string
  accountantSurname: string
  accountantPhone: string
  clientIban: string
  // Accountant
  authorizedRepresentativeEmail: string
  authorizedRepresentativeName: string
  authorizedRepresentativeSurname: string
  authorizedRepresentativePhone: string
  authorizedRepresentativeRole: string
}

export interface StakeholderDTO {
  firstName: string
  lastName: string
  type: StakeholderType
}

export enum InsuranceType {
  ALLIANZ = 'ALLIANZ',
  ATRADIUS = 'ATRADIUS',
  CREDITSAFE = 'CREDITSAFE',
  PAYMENT_HISTORY = 'PAYMENT_HISTORY',
}

export interface FinancialLimitsDTO {
  currency: Currency
  askingCreditLimit: number
  atradiusConditionCodes: string
  atradiusConditionCodes1: string
  atradiusConditionCodes2: string
  atradiusConditionDescriptions: string
  atradiusConditionDescriptions1: string
  atradiusConditionDescriptions2: string
  atradiusCreditLimit: number
  atradiusCreditLimit1: number
  atradiusCreditLimit2: number
  atradiusDecisionDate: string
  atradiusDecisionCode: string
  atradiusDecisionStatus: AtradiusDecisionStatus
  atradiusLimitPrediction: AtradiusLimit
  calculatedCreditLimit: number
  proposedPricing: number
  limitFromReport: string
  insuranceType: InsuranceType
  contractedLimit: number
  debtorStatementRequired: boolean
  debtorStatementType: DebtorStatementType
}

export enum AdditionalProduct {
  CREDIT_LINE = 'CREDIT_LINE',
  LOANS = 'LOANS',
  REVERSE_FACTORING = 'REVERSE_FACTORING',
}

export interface ProductAndPricingDTO {
  currency: Currency
  activeDebtor: number
  adminFee: number
  approvedDebtor: number
  dailyFee: number
  factoringFee: number
  factoringProduct: FactoringProductType
  feeDaysIncluded: number
  feesBasedOn: FeesType
  fundedRate: number
  gracePeriod: number
  increasedFee: number
  insuredGRate: number
  insuredRate: number
  nonRecourseFee: number
  paymentTerm: number
  creditManagementComment: string
  creditManagementType: CreditManagementType
  companyCountryCode: string
  fundingLine: FundingLine
  contractedVolume: number
  productPenalty: number
  additionalProduct: AdditionalProduct
  additionalProductComment: string
  additionalProductLimit: number
  brokerCommission: number
  brokerName: string
  channel: Channel
  clientManager: string
}

export interface RevenueDTO {
  revenue: number
  revenueFromReport: number
  year: number
}

export enum TurnoverPrediction {
  BETWEEN_150K_AND_270K = 'BETWEEN_150K_AND_270K',
  BETWEEN_270K_AND_550K = 'BETWEEN_270K_AND_550K',
  BETWEEN_70K_AND_150K = 'BETWEEN_70K_AND_150K',
  GREATER_THAN_550K = 'GREATER_THAN_550K',
  LESS_70K = 'LESS_70K',
  NOT_GIVEN = 'NOT_GIVEN',
}

export interface RatiosDTO {
  debitdaRatio: number
  debitdaRatioChange?: number
  dsoRatio: number
  dsoRatioChange?: number
  eaRatio: number
  eaRatioChange?: number
  ebit: number
  ebitChange?: number
  netProfitLoss: number
  netProfitLossChange?: number
  netSales: number
  netSalesChange?: number
  quickRatio: number
  quickRatioChange?: number
  riskRatiosPeriod: number
  workingCapitalCycle: number
  workingCapitalCycleChange?: number
  turnoverPrediction?: TurnoverPrediction
}

export interface ApproverDTO {
  approved: Date
  approverEmail: string
}

export interface ProspectDebtorDTO {
  id: string
  name: string
}

export enum ApprovalInitiationError {
  DEBTORS_LIMITS_NOT_FILLED = 'DEBTORS_LIMITS_NOT_FILLED',
  DEBTORS_PAYMENT_TERM_NOT_FILED = 'DEBTORS_PAYMENT_TERM_NOT_FILED',
  LIMITS_NOT_FILLED = 'LIMITS_NOT_FILLED',
  PAYMENT_TERM_NOT_FILED = 'PAYMENT_TERM_NOT_FILED',
  PRODUCT_INFORMATION_NOT_FILLED = 'PRODUCT_INFORMATION_NOT_FILLED',
  SALES_QUESTIONNAIRE_NOT_FILED = 'SALES_QUESTIONNAIRE_NOT_FILED',
}

export interface ApprovalInitiationErrorDTO {
  companyName?: string
  errorCode: ApprovalInitiationError
}

export interface ApprovalConditionsDTO {
  approver: string
  condition: string
  createdOn: Date
}

export enum ApprovalErrors {
  RISK_QA_NOT_FILLED = 'RISK_QA_NOT_FILLED',
}

export interface ProspectApprovalDTO {
  approvalInitiated: string
  approvalInitiatedBy: string
  approvalLevel: ApprovalLevel
  approvalRejected: Date
  approvalRejectedBy: string
  approvalRejectedComments: string
  approvalRejectedReason: ApprovalRejectedReason
  approvalStatus: ApprovalStatus
  approvalErrors: Array<ApprovalErrors>
  approvers: Array<ApproverDTO>
  approversCount: number
  approversTotalCount: number
  userCanApprove: boolean
  hitInStoplist: boolean
  debtors: Array<ProspectDebtorDTO>
  archived: string
  archivedBy: string
  archivedComments: string
  archivedReason: ArchiveReason
  clarificationInitiated: string
  clarificationInitiatedBy: string
  clarificationQuestion: string
  clarificationForAccountant: boolean
  salesCanInitiateClarification: boolean
  approvalConditions: Array<ApprovalConditionsDTO>
  clientBlocked: boolean
}

export interface DebtorApprovalDTO {
  approvalInitiated: string
  approvalInitiatedBy: string
  approvalInitiationErrors: Array<ApprovalInitiationErrorDTO>
  approvalLevel: ApprovalLevel
  approvalRejected: Date
  approvalRejectedBy: string
  approvalRejectedComments: string
  approvalRejectedReason: ApprovalRejectedReason
  approvalStatus: ApprovalStatus
  approvers: Array<ApproverDTO>
  approversCount: number
  approversTotalCount: number
  userCanApprove: boolean
  hitInStoplist: boolean
  salesCanInitiateClarification: boolean
  assignee: string
  clarificationInitiated: string
  clarificationInitiatedBy: string
  clarificationQuestion: string
}

export interface ApprovalLogsDTO {
  approver: string
  comments: string
  created: Date
  decision: ApprovalDecision | AuditActions
  rejectReason: ApprovalRejectedReason
  approvalLevel?: ApprovalLevel
  archiveReason?: ArchiveReason
}

export interface ProspectAssigneeDTO {
  riskAssignee: string
  salesAssignee: string
  assigneeNotFound: boolean
}

export enum KycRisk {
  HIGH = 'HIGH',
  LOW = 'LOW',
  NONE = 'NONE',
}

export enum ProspectDetailsVersion {
  V1 = 'V1',
  V2 = 'V2',
}

export interface ProspectApprovalRequestValidationChecklistDTO {
  atLeastOneContractWasCreated: boolean
  channelDetailsWasSet: boolean
  financingLimitWasSet: boolean
  latestFinancialsWasAttached: boolean
  overviewOfOutstandingCreditorsAttached: boolean
  salesQuestionnaireWasSet: boolean
}

export interface ProspectRiskApprovalValidationChecklistDTO {
  invoiceVerificationRulesWasSet: boolean
  riskOfferDetailsWasSet: boolean
  riskQuestionnaireWasFilled: boolean
  securityMeasuresWasAdded: boolean
  creditManagementPlanWasSet: boolean
}

export interface ProspectSalesInformationDTO {
  brokerId: string
  brokerName: string
  channel: Channel
  clientManager: string
  clientManagerEmail: string
  commissionEndDate: string
  commissionPeriod: number
}

export interface ProspectWarningsDTO {
  fieldInContractDetailsMissing: boolean
  securityMeasureDocumentMissing: boolean
  invalidContractedLimit: boolean
  __typename?: string
}

export interface ProspectContractSummaryDTO {
  administrationType: AdministrationTypes
  contractId: string
  creditLimitUpdateSettlementFee: number
  currency: Currency
  onBoardingFee: number
}

export interface ProspectErrors {
  dunsNotPresent: boolean
}

export enum ProspectLogAction {
  APPROVE = 'APPROVE',
  ARCHIVE = 'ARCHIVE',
  CLOSED_CLARIFICATION = 'CLOSED_CLARIFICATION',
  INCREASE_APPROVAL_LEVEL = 'INCREASE_APPROVAL_LEVEL',
  INITIATED = 'INITIATED',
  NEW = 'NEW',
  REJECT = 'REJECT',
  START_CLARIFICATION = 'START_CLARIFICATION',
  CREATED_FAB_CLIENT = 'CREATED_FAB_CLIENT',
  UPDATE_ASSIGNEES = 'UPDATE_ASSIGNEES',
  UPDATE_FINANCING_LIMIT = 'UPDATE_FINANCING_LIMIT',
  UPDATED_CLIENT_NAME = 'UPDATED_CLIENT_NAME',
  UPDATED_CLIENT_ADDRESS = 'UPDATED_CLIENT_ADDRESS',
  UPDATED_CLIENT_DETAILS = 'UPDATED_CLIENT_DETAILS',
  UPDATED_CLIENT_DUNS = 'UPDATED_CLIENT_DUNS',
}

export interface ProspectLogDTO {
  id: string
  action: ProspectLogAction
  approvalLevel: ApprovalLevel
  rejectReason: ApprovalRejectedReason
  archiveReason: ArchiveReason
  clarificationForAccountant: boolean
  proposedPricing: number
  proposedPricingPLN: number
  askingCreditLimit: number
  askingCreditLimitPLN: number
  calculatedCreditLimit: number
  calculatedCreditLimitPLN: number
  contractedLimit: number
  contractedLimitPLN: number
  comments: string
  fabClientId: string
  prospectId: string
  assignee: string
  riskAssignee: string
  userLogin: string
  created: Date
  companyName: string
}

export interface DunsDetailsDTO {
  city: string
  country: string
  dunsEditable: boolean
  dunsId: string
  industry: string
  legalForm: CompanyLegalForm
  postalCode: string
  registrationNumber: string
  streetAddressLine1: string
  streetAddressLine2: string
  vatNumber: string
  companyName: string
}

export interface UpdateDunsFormData {
  dunId: string
  searchType: DUNSSearchType
  companyCountryCode: string
  companyName: string
  companyAddress: string
  companyRegistrationCode: string
}

export interface ProspectDetailsDTO {
  id: string
  currency: Currency
  securityMeasuresNeedsToBeFilled: boolean
  prospectDetailsVersion: ProspectDetailsVersion
  status: ProspectStatus
  companyName: string
  companyCountryCode: string
  prospectExistsInFab: boolean
  dunsDetails: DunsDetailsDTO
  kycRisk: KycRisk
  riskAssigneeMissing: boolean
  summary: string
  hasStoplistDebtorCompanyMatch: boolean
  hasStoplistDebtorContactMatch: boolean
  hasStoplistKycMatch: boolean
  hasStoplistProspectCompanyMatch: boolean
  hasStoplistProspectContactMatch: boolean
  approvalRequestValidationChecklist: ProspectApprovalRequestValidationChecklistDTO
  riskApprovalValidationChecklist: ProspectRiskApprovalValidationChecklistDTO
  approval: ProspectApprovalDTO
  approvalLogs: Array<ApprovalLogsDTO>
  prospectSummary: ProspectSummaryDTO
  stakeholders: Array<StakeholderDTO>
  financialLimits: FinancialLimitsDTO
  financialLimitsPLN: FinancialLimitsDTO
  riskScores: RiskScoresDTO
  productAndPricing: ProductAndPricingDTO
  financialRatios: RatiosDTO
  assignees: ProspectAssigneeDTO
  salesInformation: ProspectSalesInformationDTO
  contractSummary: ProspectContractSummaryDTO
  prospectWarnings: ProspectWarningsDTO
  logs: Array<ProspectLogDTO>
}

export interface ProspectDetailsData {
  prospectDetails: ProspectDetailsDTO
}

export interface ProspectDetailsVariables {
  prospectId: string
}

export interface UpdateProspectSummaryData {
  updateProspectCompanyInfo: ProspectDetailsDTO
}

export interface UpdateProspectDunsVariables {
  prospectId: string
  dunId: string
}

export interface UpdateProspectDunsData {
  updateProspectDunsDetails: boolean
}

export interface UpdateProspectSummaryVariables {
  id: string
  industryBySales: string
  companyName: string
  companyAddressLine1: string
  companyAddressLine2: string
  companyAddressCity: string
  companyPostalCode: string
  companyCountryCode: string
  companyWebPage: string
  legalForm: CompanyLegalForm
  workingInMarketSince: string
  employeeNumber: number | string
  // Identification numbers
  vat: string
  // Contact person
  contactPersonName: string
  contactPersonSurname: string
  phone: string
  email: string
  // Accountant
  accountantEmail: string
  accountantName: string
  accountantSurname: string
  accountantPhone: string
  clientIban: string
  // Accountant
  authorizedRepresentativeEmail: string
  authorizedRepresentativeName: string
  authorizedRepresentativeSurname: string
  authorizedRepresentativePhone: string
  authorizedRepresentativeRole: string
}

export interface UpdateProspectSalesInformationData {
  updateProspectSalesInformation: ProspectDetailsDTO
}

export interface UpdateProspectSalesInformationFormVariables {
  clientManager: string
  clientManagerEmail: string
  channel: Channel
  brokerId: string
}

export interface UpdateProspectSalesInformationVariables extends UpdateProspectSalesInformationFormVariables {
  prospectId: string
}

export interface FactoringProductOption extends SelectOption {
  label: FactoringProductType
  value: FactoringProductType
}

export interface UpdateFinancialLimitsData {
  updateFinancialLimits: FinancialLimitsDTO
}

export interface UpdateFinancialLimitsVariables {
  currency: Currency
  askingCreditLimit: number
  atradiusCreditLimit?: number
  atradiusLimitPrediction?: AtradiusLimit
  calculatedCreditLimit: number
  proposedPricing: number
  id: string
}

export interface UpdateProductAndPricingData {
  updateProductAndPricing: ProductAndPricingDTO
}

export interface UpdateProductAndPricingVariables extends ProductAndPricingDTO {
  prospectId: string
}

export interface UpdateFinancialRatiosVariables extends RatiosDTO {
  prospectId: string
}

export interface UpdateFinancialRatiosData {
  updateFinancialRatios: RatiosDTO
}

export interface UpdateDebtorFinancialRatiosVariables extends RatiosDTO {
  id: string
}

export interface UpdateDebtorFinancialRatiosData {
  updateDebtorsFinancialRatios: RatiosDTO
}

export interface DebtorDTO {
  amlRisk: AmlRisk
  calculatedCreditLimit: number
  atradiusCreditLimit: number
  companyCountryCode: string
  companyName: string
  dunId: string
  expectedFactoringVolume: number
  id: string
  paymentTerm: number
  proposedCreditLimit: number
  prospectCompanyName: string
  prospectId: string
  registrationCode: string
  status: DebtorStatus
  stoplistMatch: boolean
  riskLimitFilled: boolean
  paymentTermFiled: boolean
  limitsAreFilled: boolean
  debtorQuestionnaireFilled: boolean
  currency: Currency
  insuranceType: InsuranceType
}

export interface Page_DebtorDTO {
  content: Array<DebtorDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ProspectDebtorsData {
  prospectDebtors: Page_DebtorDTO
}

export interface ProspectDebtorsVariables {
  prospectId: string
  companyName?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
  status?: Array<DebtorStatus>
  currency?: Currency[]
}

export interface AddNewProspectDebtorData {
  addProspectDebtor: DebtorDTO
}

export interface AddNewProspectDebtorVariables {
  dunId: string
  prospectId: string
}

export interface DebtorSummaryDTO {
  companyAddress?: string
  companyAddressCity?: string
  companyAddressLine1?: string
  companyAddressLine2?: string
  companyCountryCode?: string
  companyPostalCode?: string
  companyName?: string
  companyRegistrationCode?: string
  companyWebPage: string
  contactPersonFirstName: string
  contactPersonLastName: string
  countryCode?: string
  dunId: string
  email: string
  expectedFactoringVolume: number
  industry?: string
  paymentTerm: number
  phone: string
  vat?: string
  workingInMarketSince: string
  dunsEditable?: boolean
  clientIban?: string
  legalForm: CompanyLegalForm
}

export interface ProspectDebtorDetails extends DebtorSummaryDTO {
  amlRisk: AmlRisk
  status: ApprovalStatus
}

export interface DebtorApprovalInitiationChecklistDTO {
  limitsAreFilled: boolean
  paymentTermFiled: boolean
  debtorQuestionnaireFilled: boolean
}

export interface DebtorLogDTO {
  action: AuditActions
  approvalLevel: ApprovalLevel
  comments: string
  companyName: string
  createdAt: Date
  decision: ApprovalDecision
  id: string
  rejectReason: ApprovalRejectedReason
  userFullName: string
  askingCreditLimit: number
  calculatedCreditLimit: number
  proposedCreditLimit: number
}

export enum AmlRisk {
  HIGH = 'HIGH',
  LOW = 'LOW',
  NONE = 'NONE',
}

export enum AmlLabel {
  HIGH = 'HIGH',
  LOW = 'LOW',
}

export interface DebtorDetailsDTO {
  amlRisk: AmlRisk
  contractAdministrationType: AdministrationTypes
  companyCountryCode: string
  companyName: string
  fundingLine: FundingLine
  debtorSummary: DebtorSummaryDTO
  financialLimits: FinancialLimitsDTO
  dunsDetails: DunsDetailsDTO
  id: string
  prospectCompanyName: string
  prospectCurrency: Currency
  prospectCountryCode: string
  prospectExistsInFab: boolean
  prospectId: string
  prospectApprovalStatus: ApprovalStatus
  prospectDetailsVersion: ProspectDetailsVersion
  riskScores: RiskScoresDTO
  approval: DebtorApprovalDTO
  approvalInitiationChecklist: DebtorApprovalInitiationChecklistDTO
  approvalLogs: Array<ApprovalLogsDTO>
  logs: Array<DebtorLogDTO>
  financialRatios: RatiosDTO
  hasStoplistDebtorCompanyMatch: boolean
  hasStoplistDebtorContactMatch: boolean
}

export interface ProspectDebtorDetailsData {
  prospectDebtorDetails: DebtorDetailsDTO
}

export interface ProspectDebtorDetailsVariables {
  debtorId: string
}

export interface UpdateDebtorCreditLimitsData {
  updateDebtorsFinancialLimits: FinancialLimitsDTO
}

export interface UpdateDebtorCreditLimitsVariables {
  askingCreditLimit: number
  atradiusCreditLimit: number
  atradiusLimitPrediction: AtradiusLimit
  calculatedCreditLimit: number
  proposedPricing: number
  insuranceType: InsuranceType
  id: string
}

export interface UpdateProspectDebtorData {
  updateDebtorCompanyInfo: DebtorDetailsDTO
}

export interface UpdateProspectDebtorVariables {
  id: string
  companyWebPage: string
  legalForm: CompanyLegalForm
  dunId: string
  expectedFactoringVolume: number
  paymentTerm: number
  workingInMarketSince: string
  contactPersonFirstName: string
  contactPersonLastName: string
  phone: string
  email: string
}

export interface UpdateProspectDebtorFormData extends ProspectDebtorDetails {
  searchType?: DUNSSearchType
}

export interface BalanceYearDTO {
  code: string
  header: string
}

export interface BalanceSheetEntryDTO {
  aaoyAmountsOwedToCreditInstitutions: number
  aaoyAmountsPayableUnderBills: number
  aaoyDebentureLoans: number
  aaoyPaymentsReceived: number
  aaoyTradeCreditors: number
  awoyAmountsOwedToCreditInstitutions: number
  awoyAmountsPayableUnderBills: number
  awoyDebentureLoans: number
  awoyPaymentsReceived: number
  awoyTradeCreditors: number
  capital: number
  cash: number
  financials: number
  fixedAssetsOthers: number
  grantsAndSubsidies: number
  incomes: number
  intangibles: number
  prospectId: string
  annualReviewId: string
  provisions: number
  receivablesWithinYear: number
  reserves: number
  retainedProfitLoss: number
  revaluationReserve: number
  shortTermInvestments: number
  stocks: number
  tangibles: number
  accruals: number
  totalAmountPayableAndOtherLiabilities: number
  totalAmountsAfterOneYear: number
  totalAmountsWithinOneYear: number
  totalAssets: number
  totalCurrentAssets: number
  totalEquity: number
  totalEquityAndLiabilities: number
  totalFixedAssets: number
  otherShortTermAssets: number
  year: BalanceYearDTO
}

export interface ProspectsBalanceSheetEntryData {
  prospectsBalanceSheetEntry: BalanceSheetEntryDTO
}

export interface ProspectsBalanceSheetEntryVariables {
  prospectId: string
  year: string
}

export interface BalanceSheetDTO {
  aaoyAmountsOwedToCreditInstitutions: Map_String_DoubleScalar
  aaoyAmountsPayableUnderBills: Map_String_DoubleScalar
  aaoyDebentureLoans: Map_String_DoubleScalar
  aaoyPaymentsReceived: Map_String_DoubleScalar
  aaoyTradeCreditors: Map_String_DoubleScalar
  awoyAmountsOwedToCreditInstitutions: Map_String_DoubleScalar
  awoyAmountsPayableUnderBills: Map_String_DoubleScalar
  awoyDebentureLoans: Map_String_DoubleScalar
  awoyPaymentsReceived: Map_String_DoubleScalar
  awoyTradeCreditors: Map_String_DoubleScalar
  capital: Map_String_DoubleScalar
  cash: Map_String_DoubleScalar
  financials: Map_String_DoubleScalar
  fixedAssetsOthers: Map_String_DoubleScalar
  grantsAndSubsidies: Map_String_DoubleScalar
  incomes: Map_String_DoubleScalar
  intangibles: Map_String_DoubleScalar
  otherLongTermLiabilitiesAmounts: Map_String_DoubleScalar
  provisions: Map_String_DoubleScalar
  receivablesWithinYear: Map_String_DoubleScalar
  reserves: Map_String_DoubleScalar
  retainedProfitLoss: Map_String_DoubleScalar
  revaluationReserve: Map_String_DoubleScalar
  shortTermInvestments: Map_String_DoubleScalar
  stocks: Map_String_DoubleScalar
  tangibles: Map_String_DoubleScalar
  accruals: Map_String_DoubleScalar
  totalAmountPayableAndOtherLiabilities: Map_String_DoubleScalar
  totalAmountsAfterOneYear: Map_String_DoubleScalar
  totalAmountsWithinOneYear: Map_String_DoubleScalar
  totalAssets: Map_String_DoubleScalar
  totalCurrentAssets: Map_String_DoubleScalar
  totalEquity: Map_String_DoubleScalar
  totalEquityAndLiabilities: Map_String_DoubleScalar
  totalFixedAssets: Map_String_DoubleScalar
  otherShortTermAssets: Map_String_DoubleScalar
  years: Array<BalanceYearDTO>
}

export interface ProspectsBalanceSheetData {
  prospectsBalanceSheet: BalanceSheetDTO
}

export interface ProspectsBalanceSheetVariables {
  prospectId: string
}

export interface SaveBalanceSheetData {
  saveProspectsBalanceSheet: BalanceSheetEntryDTO
}

export interface SaveProspectBalanceSheetVariables extends BalanceSheetEntryDTO {
  prospectId: string
}

export interface SaveDebtorBalanceSheetVariables extends BalanceSheetEntryDTO {
  debtorId: string
}

export interface ProfitLossEntryDTO {
  administrativeCosts: number
  distributionCosts: number
  economicActivityRevenue: number
  financialYearTax: number
  grossProfitLoss: number
  netTurnover: number
  operatingCharges: number
  participationRevenue: number
  productionCost: number
  profitLossAfterTax: number
  profitLossBeforeTax: number
  profitLossFinancialYear: number
  prospectId: string
  annualReviewId: string
  securitiesRevenue: number
  operatingExpenses: number
  salesExpenses: number
  otherActivities: number
  operatingProfit: number
  gains: number
  exclusions: number
  minorityInterest: number
  netProfit: number
  otherRevenue: number
  inventoriesAndResources: number
  year: BalanceYearDTO
}

export interface ProfitLossDTO {
  administrativeCosts: Map_String_DoubleScalar
  distributionCosts: Map_String_DoubleScalar
  economicActivityRevenue: Map_String_DoubleScalar
  financialYearTax: Map_String_DoubleScalar
  grossProfitLoss: Map_String_DoubleScalar
  netTurnover: Map_String_DoubleScalar
  operatingCharges: Map_String_DoubleScalar
  participationRevenue: Map_String_DoubleScalar
  productionCost: Map_String_DoubleScalar
  profitLossAfterTax: Map_String_DoubleScalar
  profitLossBeforeTax: Map_String_DoubleScalar
  profitLossFinancialYear: Map_String_DoubleScalar
  securitiesRevenue: Map_String_DoubleScalar
  operatingExpenses: Map_String_DoubleScalar
  salesExpenses: Map_String_DoubleScalar
  otherActivities: Map_String_DoubleScalar
  operatingProfit: Map_String_DoubleScalar
  gains: Map_String_DoubleScalar
  exclusions: Map_String_DoubleScalar
  minorityInterest: Map_String_DoubleScalar
  netProfit: Map_String_DoubleScalar
  otherRevenue: Map_String_DoubleScalar
  inventoriesAndResources: Map_String_DoubleScalar
  years: Array<BalanceYearDTO>
}

export interface SaveProfitLossData {
  saveProspectsProfitLoss: ProfitLossEntryDTO
}

export interface SaveProspectProfitLossVariables extends ProfitLossEntryDTO {
  prospectId: string
}

export interface ProspectsProfitLossEntryData {
  prospectsProfitLossEntry: ProfitLossEntryDTO
}

export interface ProspectsProfitLossEntryVariables {
  prospectId: string
  year: string
}

export interface ProspectsProfitLossYearsData {
  prospectsProfitLossYears: Array<BalanceYearDTO>
}

export interface ProspectsProfitLossYearsVariables {
  prospectId: string
}

export interface ProspectsBalanceSheetYearsData {
  prospectsBalanceSheetYears: Array<BalanceYearDTO>
}

export interface ProspectsBalanceSheetYearsVariables {
  prospectId: string
}

export interface DeleteProfitLossData {
  deleteProspectsProfitLoss: BalanceYearDTO
}

export interface DeleteProfitLossVariables {
  prospectId: string
  year: string
}

export interface DeleteBalanceSheetData {
  deleteProspectsBalanceSheet: BalanceYearDTO
}

export interface DeleteBalanceSheetVariables {
  prospectId: string
  year: string
}

export interface ProspectsProfitLossData {
  prospectsProfitLoss: ProfitLossDTO
}

export interface ProspectsProfitLossVariables {
  prospectId: string
}

export interface AmortizationDTO {
  amortization: number
  years: Array<BalanceYearDTO>
}

export interface AmortizationEntryDTO {
  amortization: number
  year: BalanceYearDTO
}

export interface ProspectsAmortizationData {
  prospectsAmortization: AmortizationDTO
}

export interface ProspectsAmortizationVariables {
  prospectId: string
}

export interface ProspectsAmortizationYearsData {
  prospectsAmortizationYears: Array<BalanceYearDTO>
}

export interface ProspectsAmortizationYearsVariables {
  prospectId: string
}

export interface ProspectsAmortizationEntryData {
  prospectsAmortizationEntry: AmortizationEntryDTO
}

export interface ProspectsAmortizationEntryVariables {
  prospectId: string
  year: string
}

export interface SaveProspectAmortizationData {
  saveProspectsAmortization: AmortizationEntryDTO
}

export interface SaveProspectAmortizationVariables extends AmortizationEntryDTO {
  prospectId: string
}

export interface DeleteProspectAmortizationData {
  deleteProspectsAmortization: BalanceYearDTO
}

export interface DeleteProspectAmortizationVariables {
  prospectId: string
  year: string
}

export interface RiskRatiosDTO {
  debitdaRatio: number
  eaRatio: number
  ebit: number
  quickRatio: number
  workingCapitalCycle: number
  dsoRatio: number
  years: Array<BalanceYearDTO>
}

export interface RiskRatioEntryDTO {
  debitdaRatio: number
  eaRatio: number
  ebit: number
  quickRatio: number
  workingCapitalCycle: number
  dsoRatio: number
  prospectId: string
  annualReviewId: string
  year: BalanceYearDTO
}

export interface ProspectsRiskRatiosData {
  prospectsRiskRatios: RiskRatiosDTO
}

export interface ProspectsRiskRatiosVariables {
  prospectId: string
}

export interface ProspectsRiskRatiosYearsData {
  prospectsRiskRatiosYears: Array<BalanceYearDTO>
}

export interface ProspectsRiskRatiosYearsVariables {
  prospectId: string
}

export interface ProspectsRiskRatiosEntryData {
  prospectsRiskRatiosEntry: RiskRatioEntryDTO
}

export interface ProspectsRiskRatiosEntryVariables {
  prospectId: string
  year: string
}

export interface SaveProspectRiskRatiosData {
  saveProspectsRiskRatios: RiskRatioEntryDTO
}

export interface SaveProspectRiskRatiosVariables extends RiskRatioEntryDTO {
  prospectId: string
}

export interface DeleteProspectRiskRatiosData {
  deleteProspectsRiskRatios: BalanceYearDTO
}

export interface DeleteProspectRiskRatiosVariables {
  prospectId: string
  year: string
}

export enum AtradiusDecisionStatus {
  GOT_DECISION = 'GOT_DECISION',
  NOT_REQUESTED = 'NOT_REQUESTED',
  WAITING_FOR_DECISION = 'WAITING_FOR_DECISION',
}

export interface AtradiusDecisionDTO {
  atradiusCreditLimit: number
  atradiusCreditLimit1: number
  atradiusCreditLimit2: number
  atradiusDecisionStatus: AtradiusDecisionStatus
  atradiusDecisionStatus1: AtradiusDecisionStatus
  atradiusDecisionStatus2: AtradiusDecisionStatus
  companyCode: string
  conditionCodes: string
  conditionDescriptions: string
  conditionDescriptions1: string
  conditionDescriptions2: string
  countryCode: string
  decisionCode: string
  decisionDate: Date
  dunsId: string
  prospectId: string
}

export interface GetProspectAtradiusLimitData {
  prospectAtradiusLimit: AtradiusDecisionDTO
}

export interface GetProspectAtradiusLimitVariables {
  prospectId: string
}

export interface GetProspectAtradiusDecisionData {
  prospectAtradiusLimitDecision: AtradiusDecisionDTO
}

export interface GetProspectAtradiusDecisionVariables {
  prospectId: string
}

export interface GetDebtorAtradiusLimitData {
  debtorAtradiusLimit: AtradiusDecisionDTO
}

export interface GetDebtorAtradiusLimitVariables {
  debtorId: string
}

export interface GetDebtorAtradiusDecisionData {
  debtorAtradiusLimitDecision: AtradiusDecisionDTO
}

export interface GetDebtorAtradiusDecisionVariables {
  debtorId: string
}

export interface DebtorBalanceSheetData {
  debtorsBalanceSheet: BalanceSheetDTO
}

export interface DebtorBalanceSheetVariables {
  debtorId: string
}

export interface DebtorBalanceSheetEntryData {
  debtorsBalanceSheetEntry: BalanceSheetEntryDTO
}

export interface DebtorBalanceSheetEntryVariables {
  debtorId: string
  year: string
}

export interface DebtorProfitLossEntryData {
  debtorsProfitLossEntry: ProfitLossEntryDTO
}

export interface DebtorProfitLossEntryVariables {
  debtorId: string
  year: string
}

export interface DebtorProfitLossYearsData {
  debtorsProfitLossYears: Array<BalanceYearDTO>
}

export interface DebtorProfitLossYearsVariables {
  debtorId: string
}

export interface DebtorBalanceSheetYearsData {
  debtorsBalanceSheetYears: Array<BalanceYearDTO>
}

export interface DebtorBalanceSheetYearsVariables {
  debtorId: string
}

export interface DeleteDebtorProfitLossData {
  deleteDebtorsProfitLoss: BalanceYearDTO
}

export interface DeleteDebtorProfitLossVariables {
  debtorId: string
  year: string
}

export interface DeleteDebtorBalanceSheetData {
  deleteDebtorsBalanceSheet: BalanceYearDTO
}

export interface DeleteDebtorBalanceSheetVariables {
  debtorId: string
  year: string
}

export interface DebtorProfitLossData {
  debtorsProfitLoss: ProfitLossDTO
}

export interface DebtorProfitLossVariables {
  debtorId: string
}

export interface SaveDebtorProfitLossData {
  saveDebtorsProfitLoss: ProfitLossEntryDTO
}

export interface SaveDebtorBalanceSheetData {
  saveDebtorsBalanceSheet: BalanceSheetEntryDTO
}

export interface DebtorsAmortizationData {
  debtorsAmortization: AmortizationDTO
}

export interface DebtorsAmortizationVariables {
  debtorId: string
}

export interface DebtorsAmortizationYearsData {
  debtorsAmortizationYears: Array<BalanceYearDTO>
}

export interface DebtorsAmortizationYearsVariables {
  debtorId: string
}

export interface DebtorsAmortizationEntryData {
  debtorsAmortizationEntry: AmortizationEntryDTO
}

export interface DebtorsAmortizationEntryVariables {
  debtorId: string
  year: string
}

export interface SaveDebtorAmortizationData {
  saveDebtorsAmortization: AmortizationEntryDTO
}

export interface SaveDebtorAmortizationVariables extends AmortizationEntryDTO {
  debtorId: string
}

export interface DeleteDebtorAmortizationData {
  deleteDebtorsAmortization: BalanceYearDTO
}

export interface DeleteDebtorAmortizationVariables {
  debtorId: string
  year: string
}

export interface RiskScoresDTO {
  lastReportDate: Date
  ltScore?: number
  creditSafeProbabilityOfDefault: number
  creditSafeProviderScore: number
  creditSafeCommonValue: string
  creditSafeCreditLimit: string
  creditSafeSpRating: string
  creditSafeStatus: string
}

export interface UpdateProspectPredictionData {
  updateProspectPrediction: RiskScoresDTO
}

export interface UpdateProspectPredictionVariables {
  prospectId: string
}

export interface UpdateProspectDebtorPredictionData {
  updateProspectDebtorPrediction: RiskScoresDTO
}

export interface UpdateProspectDebtorPredictionVariables {
  debtorId: string
}

export enum ApprovalLevel {
  EYES2 = 'EYES2',
  EYES6 = 'EYES6',
  // EYES8 = 'EYES8',
}

export interface InitiateProspectApprovalData {
  initiateProspectApproval: ProspectDetailsDTO
}

export interface InitiateProspectApprovalVariables {
  prospectId: string
  comment: string
  approvalLevel: ApprovalLevel
}

export interface ApproveProspectData {
  approveProspect: ProspectDetailsDTO
}

export interface ApproveProspectVariables {
  prospectId: string
  comment: string
  debtorsForApproval?: Array<string>
}

export interface RejectProspectData {
  rejectProspect: ProspectDetailsDTO
}

export interface RejectProspectVariables {
  prospectId: string
  rejectReason: ApprovalRejectedReason
  comment?: string
}

export interface ArchiveProspectData {
  archiveProspect: ProspectDetailsDTO
}

export interface ArchivetProspectVariables {
  prospectId: string
  archiveReason: ArchiveReason
  comment?: string
}

export interface CreateClientInFABData {
  createFabClient: string
}

export interface CreateClientInFABVariables {
  prospectId: string
  sourcingAgentId?: string
  debtorsIds: Array<string>
  onboardingFeeSettlement?: number
  creditLimitFeeSettlement?: number
}

export interface CreateClientInFABFormData {
  addOnboardingFeeSettlement?: boolean
  onboardingFeeSettlement?: number
  addCreditLimitFeeSettlement?: boolean
  creditLimitFeeSettlement?: number
  debtorsIds: Array<string>
  sourcingAgentId?: string
}

export interface CloseProspectData {
  closeProspect: ProspectDetailsDTO
}

export interface CloseProspectVariables {
  prospectId: string
  closeReason: ApprovalCloseReason
  comment: string
}

export interface StartClarificationProspectData {
  startClarificationProspect: ProspectDetailsDTO
}

export interface StartClarificationProspectVariables {
  prospectId: string
  comment: string
  clarificationForAccountant: boolean
}

export interface FinishClarificationProspectData {
  finishClarificationProspect: ProspectDetailsDTO
}

export interface FinisClarificationProspectVariables {
  prospectId: string
}

export interface InitiateDebtorApprovalData {
  initiateDebtorApproval: DebtorDetailsDTO
}

export interface InitiateDebtorApprovalVariables {
  debtorId: string
  comment: string
  approvalLevel: ApprovalLevel
}

export interface ResetToNewProspectData {
  resetToNewProspect: ProspectDetailsDTO
}

export interface ResetToNewProspectVariables {
  prospectId: string
  comment?: string
}

export interface ResetToNewProspectFormValues {
  comment: string
}

export enum ResetToNewProspectFieldsSchema {
  COMMENT = 'comment',
}

export interface ApproveDebtorData {
  approveDebtor: DebtorDetailsDTO
}

export interface ApproveDebtorVariables {
  debtorId: string
  comment?: string
}

export interface RejectDebtorData {
  rejectDebtor: DebtorDetailsDTO
}

export interface RejectDebtorVariables {
  debtorId: string
  rejectReason: ApprovalRejectedReason
  comment?: string
}

export interface StartClarificationDebtorData {
  startClarificationDebtor: DebtorDetailsDTO
}

export interface StartClarificationDebtorVariables {
  debtorId: string
  comment: string
}

export interface FinishClarificationDebtorData {
  finishClarificationDebtor: DebtorDetailsDTO
}

export interface FinisClarificationDebtorVariables {
  debtorId: string
}

export interface CloseDebtorData {
  closeDebtor: DebtorDetailsDTO
}

export interface CloseDebtorVariables {
  debtorId: string
  closeReason: ApprovalCloseReason
  comment: string
}

export interface AssigneeDTO {
  email: string
  fullname: string
}

export interface ProspectSalesAssigneesData {
  prospectSalesAssignees: Array<AssigneeDTO>
}

export interface ProspectRiskAssigneesData {
  prospectRiskAssignees: Array<AssigneeDTO>
}

export interface UpdateSummaryData {
  updateProspectsSummary: ProspectDetailsDTO
}

export interface UpdateSummaryVariables {
  id: string
  summary: string
}

export interface UpdateProspectAssigneesData {
  updateProspectsAssignees: ProspectDetailsDTO
}

export interface UpdateProspectAssigneesVariables {
  id?: string
  riskAssignee: string
  salesAssignee: string
}

export enum ReportType {
  AGREEMENT_WITH_DEBTOR = 'AGREEMENT_WITH_DEBTOR',
  ANNUAL_AND_INTERIM_FINANCIALS = 'ANNUAL_AND_INTERIM_FINANCIALS',
  ANNUAL_REPORT = 'ANNUAL_REPORT',
  CLIENT_FINANCIALS = 'CLIENT_FINANCIALS',
  COMPANY_STRUCTURE = 'COMPANY_STRUCTURE',
  COPY_OF_BANK_STATEMENT = 'COPY_OF_BANK_STATEMENT',
  CREDITINFO = 'CREDITINFO',
  CREDIT_REPORT = 'CREDIT_REPORT',
  CREDITSAFE = 'CREDITSAFE',
  CREDITWEB = 'CREDITWEB',
  CSV = 'CSV',
  DEBTOR_STATEMENT = 'DEBTOR_STATEMENT',
  EXCEPTION_FROM_HEAD_OF_RISK = 'EXCEPTION_FROM_HEAD_OF_RISK',
  EXPERIAN = 'EXPERIAN',
  INVOICE_EXAMPLE = 'INVOICE_EXAMPLE',
  KIB = 'KIB',
  KYC_DOCUMENT = 'KYC_DOCUMENT',
  ONDATO_API = 'ONDATO_API',
  ONDATO_STOPLIST = 'ONDATO_STOPLIST',
  OVERVIEW_OF_OUTSTANDING_DEBTORS = 'OVERVIEW_OF_OUTSTANDING_DEBTORS',
  RISK_DECISION = 'RISK_DECISION',
  RISK_PREDICTION = 'RISK_PREDICTION',
  RPA = 'RPA',
  SECURITY_MEASURE = 'SECURITY_MEASURE',
  WISE_FUNDING_REPORT = 'WISE_FUNDING_REPORT',
  OTHER = 'OTHER',
}

export enum ProspectReportType {
  AGREEMENT_WITH_DEBTOR = 'AGREEMENT_WITH_DEBTOR',
  ANNUAL_AND_INTERIM_FINANCIALS = 'ANNUAL_AND_INTERIM_FINANCIALS',
  COMPANY_STRUCTURE = 'COMPANY_STRUCTURE',
  COPY_OF_BANK_STATEMENT = 'COPY_OF_BANK_STATEMENT',
  CREDIT_REPORT = 'CREDIT_REPORT',
  EXCEPTION_FROM_HEAD_OF_RISK = 'EXCEPTION_FROM_HEAD_OF_RISK',
  INVOICE_EXAMPLE = 'INVOICE_EXAMPLE',
  KYC_DOCUMENT = 'KYC_DOCUMENT',
  OVERVIEW_OF_OUTSTANDING_DEBTORS = 'OVERVIEW_OF_OUTSTANDING_DEBTORS',
  RISK_DECISION = 'RISK_DECISION',
  RISK_PREDICTION = 'RISK_PREDICTION',
  RPA = 'RPA',
  SECURITY_MEASURE = 'SECURITY_MEASURE',
  OTHER = 'OTHER',
}

export enum DebtorReportType {
  AGREEMENT_WITH_DEBTOR = 'AGREEMENT_WITH_DEBTOR',
  ANNUAL_AND_INTERIM_FINANCIALS = 'ANNUAL_AND_INTERIM_FINANCIALS',
  COMPANY_STRUCTURE = 'COMPANY_STRUCTURE',
  COPY_OF_BANK_STATEMENT = 'COPY_OF_BANK_STATEMENT',
  CREDIT_REPORT = 'CREDIT_REPORT',
  DEBTOR_STATEMENT = 'DEBTOR_STATEMENT',
  EXCEPTION_FROM_HEAD_OF_RISK = 'EXCEPTION_FROM_HEAD_OF_RISK',
  INVOICE_EXAMPLE = 'INVOICE_EXAMPLE',
  KYC_DOCUMENT = 'KYC_DOCUMENT',
  OVERVIEW_OF_OUTSTANDING_DEBTORS = 'OVERVIEW_OF_OUTSTANDING_DEBTORS',
  RISK_DECISION = 'RISK_DECISION',
  RISK_PREDICTION = 'RISK_PREDICTION',
  RPA = 'RPA',
  SECURITY_MEASURE = 'SECURITY_MEASURE',
  OTHER = 'OTHER',
}

export interface ProspectReportFileDTO {
  content: string
  documentId: string
  fileName: string
  prospectId: string
  description: string
  reportCreatedAt: string
  reportType: ReportType
  sendReportToReceivers: boolean
  uploader: string
}

export interface Page_ProspectReportFileDTO {
  content: Array<ProspectReportFileDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ProspectReportsData {
  prospectReports: Page_ProspectReportFileDTO
}

export interface ProspectReportsVariables {
  prospectId: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface DownloadProspectReportData {
  downloadProspectReport: ProspectReportFileDTO
}

export interface DownloadProspectReportVariables {
  documentId: string
}

export interface UploadProspectReportData {
  uploadProspectReport: string
}

export interface UploadProspectReportVariables {
  reportType: ReportType | string
  prospectId?: string
  sendReportToReceivers?: boolean
  file: ReportFileDTOInput | string
  reportCreatedAt: string
  description?: string
  index: number
}

export interface UploadReportFormValues {
  reportType: ReportType | string
  prospectId?: string
  sendReportToReceivers?: boolean
  files: Array<ReportFileDTOInput | string>
  reportCreatedAt: string
  description?: string
}

export interface DebtorReportFileDTO {
  content: string
  description: string
  documentId: string
  fileName: string
  prospectId: string
  reportCreatedAt: string
  reportType: ReportType
  sendReportToReceivers: boolean
  uploader: string
}

export interface Page_DebtorReportFileDTO {
  content: Array<DebtorReportFileDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface DebtorReportsData {
  debtorReports: Page_DebtorReportFileDTO
}

export interface DebtorReportsVariables {
  debtorId: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface DownloadDebtorReportData {
  downloadDebtorReport: ProspectReportFileDTO
}

export interface DownloadDebtorReportVariables {
  documentId: string
}

export interface UploadDebtorReportData {
  uploadDebtorReport: string
}

export interface UploadDebtorReportVariables {
  reportType: ReportType
  debtorId?: string
  sendReportToReceivers: boolean
  file: ReportFileDTOInput
  reportCreatedAt: string
  description?: string
  index: number
}

export interface UploadDebtorReportFormValues {
  reportType: ReportType
  debtorId?: string
  sendReportToReceivers: boolean
  files: ReportFileDTOInput[]
  reportCreatedAt: string
  description?: string
}

export enum TaskEvent {
  DEBTOR_PENDING = 'DEBTOR_PENDING',
  PROSPECT_APPROVED = 'PROSPECT_APPROVED',
  PROSPECT_PENDING = 'PROSPECT_PENDING',
  PROSPECT_REJECTED = 'PROSPECT_REJECTED',
  PROSPECT_NEW = 'PROSPECT_NEW',
}

export interface TaskDTO {
  taskEvent: TaskEvent
  approvalLevel: ApprovalLevel
  approvalStatus: ApprovalStatus
  approvedByTaskReceiver: boolean
  approversCount: number
  approversTotalCount: number
  assignee: string
  companyName: string
  countryCode: string
  dealId: number
  dunId: string
  prospectId: string
  debtorId: string
  registrationCode: string
  riskAssignee: string
  taskReceiver: string
}

export interface Page_TaskDTO {
  content: Array<TaskDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface TasksData {
  tasks: Page_TaskDTO
}

export interface TasksVariables {
  filter?: string
  dealId?: number
  pageNum?: number
  pageSize?: number
  sortDirection?: Direction
  sortField?: string
}

export interface ClarificationCommentDTO {
  comment: string
  createdOn: string
  fullName: string
  usersLogin: string
}

export interface AddProspectClarificationData {
  addProspectClarificationComment: Array<ClarificationDTO>
}

export interface AddProspectClarificationVariables {
  prospectId: string
  comment: string
}

export interface ProspectClarificationsData {
  prospectClarifications: Array<ClarificationDTO>
}

export interface ProspectClarificationsVariables {
  prospectId: string
}

export interface AddDebtorClarificationData {
  addDebtorClarificationComment: Array<ClarificationDTO>
}

export interface AddDebtorClarificationVariables {
  debtorId: string
  comment: string
}

export interface DebtorClarificationsData {
  debtorClarifications: Array<ClarificationDTO>
}

export interface DebtorClarificationsVariables {
  debtorId: string
}

export enum KycFailureReason {
  ATA_DOES_NOT_MATCH = 'ATA_DOES_NOT_MATCH',
  BAD_MEDIA_FORMAT = 'BAD_MEDIA_FORMAT',
  BLURRED_PHOTO = 'BLURRED_PHOTO',
  DOCUMENT_IS_EXPIRED = 'DOCUMENT_IS_EXPIRED',
  DOCUMENT_NOT_ACCEPTED = 'DOCUMENT_NOT_ACCEPTED',
  DOCUMENT_WITH_NON_LATIN_CHARACTERS = 'DOCUMENT_WITH_NON_LATIN_CHARACTERS',
  EXPIRED_IDENTIFICATION = 'EXPIRED_IDENTIFICATION',
  FACES_DOES_NOT_MATCH = 'FACES_DOES_NOT_MATCH',
  MISCELLANEOUS = 'MISCELLANEOUS',
  MISSING_DOCUMENT_PHOTO = 'MISSING_DOCUMENT_PHOTO',
  MISSING_SELFIE_PHOTO = 'MISSING_SELFIE_PHOTO',
  MORE_THAN_ONE_PERSON = 'MORE_THAN_ONE_PERSON',
  PART_OF_DOCUMENT_IS_COVERED = 'PART_OF_DOCUMENT_IS_COVERED',
  PART_OF_FACE_IS_COVERED = 'PART_OF_FACE_IS_COVERED',
  POOR_PHOTO_LIGHTING = 'POOR_PHOTO_LIGHTING',
  POOR_PHOTO_QUALITY = 'POOR_PHOTO_QUALITY',
  POSSIBLE_FRAUD_ATTEMPT = 'POSSIBLE_FRAUD_ATTEMPT',
  PROHIBITED_COUNTRY_OR_STATE = 'PROHIBITED_COUNTRY_OR_STATE',
  SANCTIONED = 'SANCTIONED',
  UNDEFINED = 'UNDEFINED',
  UNDERAGE_PERSON = 'UNDERAGE_PERSON',
  UNRELATED_PHOTO = 'UNRELATED_PHOTO',
}

export enum KycIdentityRole {
  BENEFICIAL_OWNER = 'BENEFICIAL_OWNER',
  LEGAL_REPRESENTATIVE = 'LEGAL_REPRESENTATIVE',
}

export enum KycQuestionnaireStatus {
  APPROVED = 'APPROVED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  SUCCESSFUL = 'SUCCESSFUL',
}

export interface KycIdentityDTO {
  country: string
  email: string
  failureReason: KycFailureReason
  id: string
  name: string
  questionnaireUrl: string
  role: KycIdentityRole
  status: KycQuestionnaireStatus
  surname: string
  communicationCountry: string
  ondatoBackOfficeStatusUrl: string
  birthYear: number
  stoplistMatch: boolean
}

export interface ProspectKycIdentityDTO extends KycIdentityDTO {
  prospectId: string

  [key: string]: string | ReactNode
}

export interface ProspectKycIdentitiesData {
  prospectKycIdentities: Array<ProspectKycIdentityDTO>
}

export interface ProspectKycIdentitiesVariables {
  prospectId: string
}

export interface ProspectKycIdentityData {
  prospectKycIdentity: ProspectKycIdentityDTO
}

export interface ProspectKycIdentityVariables {
  id: string
}

export interface CheckProspectKycIdentityData {
  checkProspectKycStatus: Array<ProspectKycIdentityDTO>
}

export interface CheckProspectKycIdentityVariables {
  id: string
}

export interface StartProspectKycData {
  startProspectKyc: Array<ProspectKycIdentityDTO>
}

export interface StartProspectKycVariables {
  id: string
}

export interface AddProspectKycIdentityData {
  addProspectKycIdentity: ProspectKycIdentityDTO
}

export interface AddKycIdentityVariables {
  country: string
  role: KycIdentityRole | string
  surname: string
  name: string
  email: string
}

export interface AddProspectKycIdentityVariables extends AddKycIdentityVariables {
  prospectId: string
}

export interface AddProspectKycIdentityVariables extends AddKycIdentityVariables {
  prospectId: string
}

export interface DeleteProspectKycIdentityData {
  deleteProspectKycIdentity: Array<ProspectKycIdentityDTO>
}

export interface DeleteProspectKycIdentityVariables {
  id: string
}

export interface EditProspectKycIdentityData {
  updateProspectKycIdentity: ProspectKycIdentityDTO
}

export interface EditKycIdentityVariables {
  country: string
  role: KycIdentityRole
  surname: string
  name: string
  email: string
}

export interface EditProspectKycIdentityVariables extends EditKycIdentityVariables {
  prospectId: string
}

export interface CompanyDirectorDTOInput {
  amountOfShares: string
  companyDetails: string
}

export interface CreditorDTOInput {
  amount: number
  creditor: string
  pledge: string
}

export enum LeadSource {
  MARKETING_EMAIL_CAMPAIGN = 'MARKETING_EMAIL_CAMPAIGN',
  MARKETING_EVENTS = 'MARKETING_EVENTS',
  MARKETING_FACEBOOK = 'MARKETING_FACEBOOK',
  MARKETING_GOOGLE_ADS = 'MARKETING_GOOGLE_ADS',
  MARKETING_GOOGLE_DIRECT = 'MARKETING_GOOGLE_DIRECT',
  MARKETING_GOOGLE_ORGANIC = 'MARKETING_GOOGLE_ORGANIC',
  MARKETING_GOOGLE_REFERRAL = 'MARKETING_GOOGLE_REFERRAL',
  MARKETING_IB_CALL = 'MARKETING_IB_CALL',
  MARKETING_IB_REQUEST = 'MARKETING_IB_REQUEST',
  MARKETING_LINKED_HELPER = 'MARKETING_LINKED_HELPER',
  MARKETING_LINKEDIN = 'MARKETING_LINKEDIN',
  MARKETING_NEWSLETTER = 'MARKETING_NEWSLETTER',
  MARKETING_RECOMMENDATION = 'MARKETING_RECOMMENDATION',
  SALES_BROKER_LT1 = 'SALES_BROKER_LT1',
  SALES_COLD_CALL = 'SALES_COLD_CALL',
  SALES_LINKEDIN = 'SALES_LINKEDIN',
  SALES_DIRECT = 'SALES_DIRECT',
  SALES_RECOMMENDATION = 'SALES_RECOMMENDATION',
  SALES_RELATIONSHIP = 'SALES_RELATIONSHIP',
}

export enum YesNoInDocsChoice {
  IN_DOCS = 'IN_DOCS',
  NO = 'NO',
  YES = 'YES',
}

export interface SalesQuestionnairesDTO {
  clientWillingGiveGuaranteeComments: string
  companyDirectors: [CompanyDirectorDTOInput]
  numberOfDebtor: number
  permissionToContactAccountant: boolean
  creditFacilityWithBank: boolean
  creditFacilityWithBankChoice: YesNoInDocsChoice
  creditFacilityWithBankComments: string
  companyBelongsToGroup: boolean
  clientWillingGiveGuarantee: boolean
  debtRepayment: boolean
  clientActivitySector: string
  visitDate: Date
  clientVisitComments: string
  businessOfExpansion: boolean
  pcaName: string
  pcaSurname: string
  purchaseOfAssets: boolean
  clientInvolvedInLawsuit: boolean
  clientVisit: boolean
  creditors: [CreditorDTOInput]
  refinancing: boolean
  personalIdSeen: boolean
  workingCapital: boolean
  pcaPhone: string
  accountUsed: boolean
  clientInvolvedInLawsuitComments: string
  companyDirector: boolean
  purposeOfFundingComments: string
  visitedPlaceAddress: string
  personWhoVisited: string
  organizationStructureAttached: boolean
  pcaEmail: string
  purposeOfFundingOther: boolean
  outstandingReceivablesOverdue: boolean
  outstandingReceivablesOverdueAmount: number
  outstandingReceivablesOverdueChoice: YesNoInDocsChoice
  outstandingReceivablesOverdueComments: string
  havingUnpaidLiabilities: boolean
  havingUnpaidLiabilitiesComments: string
  allDocumentsAttached: boolean
  allDocumentsAttachedComments: string
  leadSource: LeadSource
}

export interface SalesSummaryQuestionnaireDTO {
  additionalInformation: string
  averageAmountPerInvoice: string
  contractInPlace: boolean
  contractInPlaceComment: string
  daysSalesOutstanding: string
  debtorStatementAgreedToBeSigned: boolean
  debtorStatementAgreedToBeSignedComment: string
  expectedAverageAmountPerInvoice: string
  expectedMonthlyTurnover: string
  expectedPaymentTerm: string
  howOftenSellerInvoice: string
  invoicingProcess: string
  lengthOfTradingExperience: string
  monthlyTurnover: string
  overdue: string
  paymentExperience: boolean
  paymentTerm: string
  prospectId: string
  shortDescription: string
  supportingDocuments: string
}

export interface SalesQuestionnairesDetailsDTO {
  salesQuestionnaires: SalesQuestionnairesDTO
  salesSummaryQuestionnaire: SalesSummaryQuestionnaireDTO
  summary: string
}

export interface SalesQuestionnairesDetailsData {
  prospectsSalesQuestionnaires: SalesQuestionnairesDetailsDTO
}

export interface SalesQuestionnairesDetailsVariables {
  id: string
}

export interface UpdateSalesQuestionnaireData {
  updateProspectsSalesQuestionnaires: SalesQuestionnairesDetailsDTO
}

export interface UpdateSalesQuestionnaireVariables extends SalesQuestionnairesDTO {
  id: string
}

export interface UpdateSalesSummaryQuestionnaireData {
  updateProspectsSalesSummaryQuestionnaire: SalesQuestionnairesDetailsDTO
}

export interface UpdateSalesSummaryQuestionnaireVariables extends SalesSummaryQuestionnaireDTO {
  id: string
}

export interface RiskQuestionnairesDTO {
  accountReceivablesPledged: boolean
  accountReceivablesPledgedComments: string
  accountantOfficeAddress: string
  callWithAccountant: boolean
  changeToForbiddenIndustry: boolean
  changesInSixMonths: boolean
  changesInSixMonthsComments: string
  clientHasActiveLawsuit: boolean
  clientHasDelaysInTaxes: boolean
  companyWorkWithThisAccountant: string
  concentrationLimitExceeded: boolean
  concentrationLimitExceededComments: string
  conversationDuration: string
  dateOfAccountantSpoken: string
  debtorsPledge: string
  headquartersNotInEurope: boolean
  hitForMandatoryGuarantee: boolean
  hitInEligibility: boolean
  hitInKyc: boolean
  hitInKycComments: string
  hitInternalStopList: boolean
  hitInternalStopListComments: string
  hitOngoingBankruptcy: boolean
  hitOngoingBankruptcyComments: string
  legalStatus: boolean
  limitGt100KAndEquityLs50Proc: boolean
  negativeEquity: boolean
  propertyOwnsOrRents: string
  revenue: boolean
  siteVisitReportCheck: boolean
  siteVisitReportCheckComments: string
  taxDebts: string
  tradingHistory: boolean
  unprofitableActivity: boolean
  webDomainCheck: boolean
  webDomainCheckComments: string
  workingInMarket: boolean
  irregularities: boolean
  prospectCompanyFound: boolean
  prospectCompanyFoundComments: string
  prospectRepresentativeFound: boolean
  prospectRepresentativeFoundComments: string
}

export interface RiskQuestionnairesDetailsDTO {
  riskQuestionnaires: RiskQuestionnairesDTO
  summary: string
  representativeCompanies: Array<RepresentativeCompanyDTO>
  representatives: Array<RepresentativeDTO>
  relatedCompanies: Array<ClientRelatedCompanyDTO>
}

export interface RiskQuestionnairesDetailsData {
  prospectsRiskQuestionnaires: RiskQuestionnairesDetailsDTO
}

export interface RiskQuestionnairesDetailsVariables {
  id: string
}

export interface UpdateRiskQuestionnaireData {
  updateProspectsRiskQuestionnaires: RiskQuestionnairesDetailsDTO
}

export interface UpdateRiskQuestionnaireVariables extends RiskQuestionnairesDTO {
  id: string
}

export interface UpdateRiskSummaryData {
  updateProspectsRiskQuestionnairesSummary: RiskQuestionnairesDetailsDTO
}

export interface UpdateRiskSummaryVariables {
  id: string
  summary: string
}

export enum FactoringProduct {
  AMERICAN_FACTORING_RECOURSE = 'AMERICAN_FACTORING_RECOURSE',
  AMERICAN_FACTORING_NON_RECOURSE = 'AMERICAN_FACTORING_NON_RECOURSE',
  INVOICE_FINANCING_RECOURSE = 'INVOICE_FINANCING_RECOURSE',
  INVOICE_FINANCING_NON_RECOURSE = 'INVOICE_FINANCING_NON_RECOURSE',
  INVOICE_DISCOUNTING_NON_RECOURSE = 'INVOICE_DISCOUNTING_NON_RECOURSE',
  INVOICE_FINANCING_RECOURSE_INVEGA = 'INVOICE_FINANCING_RECOURSE_INVEGA',
  INVOICE_FINANCING_NON_RECOURSE_INVEGA = 'INVOICE_FINANCING_NON_RECOURSE_INVEGA',
  INVOICE_FINANCING_SILENT = 'INVOICE_FINANCING_SILENT',
  LOANS = 'LOANS',
  CREDIT_LINE = 'CREDIT_LINE',
  REVERSE_FACTORING = 'REVERSE_FACTORING',
  N_A = 'N_A',
}

export enum MainProblem {
  BANKRUPTCY = 'BANKRUPTCY',
  CREDIT_RISK = 'CREDIT_RISK',
  FAKE_ACCOUNTANT = 'FAKE_ACCOUNTANT',
  FRAUD = 'FRAUD',
  MISDIRECTION = 'MISDIRECTION',
  OFFSET = 'OFFSET',
  OPERATIONAL_MISTAKE = 'OPERATIONAL_MISTAKE',
  OVERDUE = 'OVERDUE',
  N_A = 'N_A',
}

export interface ProspectApprovalInitiationErrorDTO {
  errors: Array<ApprovalInitiationErrorDTO>
}

export interface CanInitiateProspectApprovalData {
  canInitiateProspectApproval: ProspectApprovalInitiationErrorDTO
}

export interface CanInitiateProspectApprovalVariables {
  id: string
}

export interface IncreaseProspectApprovalLevelData {
  increaseProspectApprovalLevel: ProspectDetailsDTO
}

export interface IncreaseProspectApprovalLevelVariables {
  prospectId: string
}

export interface IncreaseProspectDebtorApprovalLevelData {
  increaseDebtorApprovalLevel: DebtorDetailsDTO
}

export interface IncreaseProspectDebtorApprovalLevelVariables {
  debtorId: string
}

export enum InsuranceSource {
  CREDIT_REPORT = 'CREDIT_REPORT',
  NOT_INSURED = 'NOT_INSURED',
  PORTAL = 'PORTAL',
}

export interface DebtorRiskQuestionnairesDTO {
  companyHitInOndato: boolean
  companyHitInOndatoComment: string
  concentrationBreachPerFundingLine: boolean
  concentrationBreachPerFundingLineComment: string
  hitCountrySPRatingHigh: boolean
  hitCountrySPRatingHighComment: string
  hitCurrentRatioLessEqualOne: YesNoNaChoice
  hitCurrentRatioLessEqualOneComment: string
  hitEquityLessFifteen: YesNoNaChoice
  hitEquityLessFifteenComment: string
  hitInMarketLessOneYear: boolean
  hitInMarketLessOneYearComment: string
  hitInMarketLessTwoYears: boolean
  hitInMarketLessTwoYearsComment: string
  hitInStopList: boolean
  hitInStopListComment: string
  hitProfitabilityLessZero: YesNoNaChoice
  hitProfitabilityLessZeroComment: string
  id: string
  notInOECDList: boolean
  notInOECDListComment: string
  notReceivingInsurance: boolean
  notReceivingInsuranceComment: string
  personHitInOndato: boolean
  personHitInOndatoComment: string
  prospectCountryCode: string
  receivedInsuranceLimit: boolean
  receivedInsuranceLimitComments: string
  summary?: string
  contract?: YesNoNaChoice
  contractComments?: string
  websiteCheck?: boolean
  websiteCheckComments?: string
  internalTrackRecord?: boolean
  internalTrackRecordComments?: string
  debtorCompanyFound: boolean
  debtorCompanyFoundComments: string
  debtorRepresentativeFound: boolean
  debtorRepresentativeFoundComments: string
  hitNoRecentPublishedAccounts?: boolean
  hitNoRecentPublishedAccountsComment?: string
  hitRecentNegativeInformation?: YesNoNaChoice
  hitRecentNegativeInformationComment?: string
  hitCompanyChangesInSixMonths?: boolean
  hitCompanyChangesInSixMonthsComment?: string
  hitBranchOfForeignCompany?: boolean
  hitBranchOfForeignCompanyComment?: string
  representativeCompanies: Array<RepresentativeCompanyDTO>
  representatives: Array<RepresentativeDTO>
  relatedCompanies: Array<ClientRelatedCompanyDTO>
}

export interface DebtorRiskQuestionnairesData {
  debtorsRiskQuestionnaires: DebtorRiskQuestionnairesDTO
}

export interface DebtorRiskQuestionnairesVariables {
  id: string
}

export interface UpdateProspectDebtorRiskQuestionnaireData {
  updateDebtorsRiskQuestionnaires: DebtorRiskQuestionnairesDTO
}

export interface UpdateProspectDebtorRiskQuestionnaireVariables extends DebtorRiskQuestionnairesDTO {}

export interface UpdateDebtorRiskSummaryData {
  updateDebtorsRiskQuestionnairesSummary: DebtorRiskQuestionnairesDTO
}

export interface UpdateDebtorRiskSummaryVariables {
  id: string
  summary: string
}

export interface SourcingAgentDTO {
  companyName: string
  id: string
  sourcingAgentAdministrates: string
}

export interface SourcingAgentsData {
  sourcingAgent: Array<SourcingAgentDTO>
}

export interface SourcingAgentsVariables {
  spv: boolean
}

export interface ProspectDebtorsListData {
  listProspectDebtors: Array<DebtorDTO>
}

export interface ProspectDebtorsListVariables {
  prospectId: string
}

export enum ActionFieldsSchema {
  COMMENT = 'comment',
  COMMENT_TYPE = 'commentType',
  DEBTORS_FOR_APPROVAL = 'debtorsForApproval',
  REASON = 'reason',
  WITH_REASON = 'withReason',
  WITH_APPROVAL_LEVEL = 'withApprovalLevel',
  CLARIFICATION_FOR_ACCOUNTANT = 'clarificationForAccountant',
  APPROVAL_LEVEL = 'approvalLevel',
}

export interface ActionFormValues {
  comment: string
  commentType: CommentType
  debtorsForApproval?: Array<string>
  reason?: ArchiveReason | ApprovalRejectedReason | ApprovalCloseReason
  withApprovalLevel: boolean
  withReason: boolean
  approvalLevel?: ApprovalLevel
  clarificationForAccountant?: boolean
}

export interface UpdateProspectRepresentativesData {
  updateProspectRepresentatives: RepresentativeDTO
}

export interface UpdateProspectRepresentativesVariables {
  id: string
  representatives: Array<RepresentativeDTO>
}

export interface UpdateProspectRepresentativesCompaniesData {
  updateProspectRepresentativeCompanies: RepresentativeDTO
}

export interface UpdateProspectRepresentativesCompaniesVariables {
  id: string
  representatives: Array<string>
}

export interface UpdateProspectRelatedCompaniesData {
  updateProspectRelatedCompanies: RepresentativeDTO
}

export interface UpdateProspectRelatedCompaniesVariables {
  id: string
  relatedCompanies: Array<string>
}

export interface UpdateProspectDebtorRepresentativesData {
  updateProspectDebtorRepresentatives: RepresentativeDTO
}

export interface UpdateProspectDebtorRepresentativesVariables {
  id: string
  representatives: Array<RepresentativeDTO>
}

export interface UpdateProspectDebtorRepresentativesCompaniesData {
  updateProspectDebtorRepresentativeCompanies: RepresentativeDTO
}

export interface UpdateProspectDebtorRepresentativesCompaniesVariables {
  id: string
  representatives: Array<string>
}

export interface UpdateProspectDebtorRelatedCompaniesData {
  updateProspectDebtorRelatedCompanies: RepresentativeDTO
}

export interface UpdateProspectDebtorRelatedCompaniesVariables {
  id: string
  relatedCompanies: Array<string>
}

export enum NewProspectContractFieldNames {
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  CONTRACT_TYPE_ID = 'contractTypeId',
  CONTRACT_TYPE_NAME = 'contractTypeName',
  CURRENCY = 'currency',
  FUNDING_LINE = 'fundingLine',
  FACTORING_PRODUCT = 'factoringProduct',
  START_DATE = 'signingDate',
  END_DATE = 'endDate',
  DURATION = 'duration',
}

export interface ProspectContractChecklistDTO {
  contractDetailsWasSet: boolean
  debtorsAreIncluded: boolean
  prospectDetailsWasSet: boolean
}

export interface ProspectContractDTO {
  administrationFee: number
  contractName: string
  contractTypeId: string
  contractTypeName: string
  contractedVolume: number
  creditLimitUpdateSettlementFee: number
  currency: Currency
  dailyFee: number
  duration: number
  endDate: string
  factoringProduct: FactoringProduct
  financingIncreaseFee: number
  financingIncreaseFeeMinimum: number
  fundingLine: FundingLine
  gracePeriod: number
  id: string
  increasedFee: number
  insuranceType: InsuranceType
  insuredRate: number
  monthlyFee: number
  nonRecourseFee: number
  nonRecourseFeeMinimum: number
  onBoardingFee: number
  onboardingFeeInPercentage: number
  onboardingFeeType: OnboardingFeeType
  paymentTerm: number
  penaltyOnVolume: number
  prospectId: string
  prospectName: string
  purchaseRate: number
  renewalFee: number
  renewalFeeInPercentage: number
  renewalFeeType: RenewalFeeType
  signingDate: string
  sourcingAgentAdministrates: AdministrationTypes
  transferFee: number
}

export interface ProspectContractDebtorDTO {
  debtorId: string
  debtorName: string
}

export interface ProspectContractAuditDTO {
  action: AuditActions
  createdAt: Date
  id: string
  userFullName: string
}

export interface ProspectContractDetailsDTO {
  prospectId: string
  prospectName: string
  prospectEmails: string[]
  prospectStatus: ProspectStatus
  contractedLimit: number
  contractedLimitPLN: number
  approvalChecklist: ProspectContractChecklistDTO
  debtors: Array<ProspectContractDebtorDTO>
  contract: ContractFeeDetailsDTO
  logs: Array<ProspectContractAuditDTO>
}

export interface AddNewProspectContractData {
  createProspectContract: ProspectContractDTO
}

export interface AddNewProspectContractFormValues {
  sourcingAgentAdministrates: AdministrationTypes
  duration: number
  fundingLine: FundingLine | string
  factoringProduct: FactoringProduct | string
  signingDate: string
  endDate: string
  contractTypeId: string
  currency: Currency | string
}

export interface AddNewProspectContractVariables extends AddNewProspectContractFormValues {
  prospectId: string
}

export interface ProspectContractsData {
  prospectContracts: Array<ProspectContractDTO>
}

export interface ProspectContractsVariables {
  prospectId: string
}

export interface ProspectContractData {
  prospectContractDetails: ProspectContractDetailsDTO
}

export interface ProspectContractVariables {
  contractId: string
}

export interface UpdateProspectDebtorToContractData {
  updateDebtorsToProspectContract: ProspectContractDetailsDTO
}

export interface UpdateProspectDebtorToContractFormValues {
  debtorIds: string[]
}

export enum UpdateProspectDebtorToContractFieldsNames {
  PROSPECT_DEBTORS_IDS = 'debtorIds',
}

export interface UpdateProspectDebtorToContractVariables extends UpdateProspectDebtorToContractFormValues {
  contractId: string
}

export interface UpdateProspectContractData {
  updateProspectContract: ProspectContractDTO
}

export interface UpdateProspectContractVariables extends ContractFeeDetailsDTO {
  contractId: string
}

export interface RiskOfferInvoiceVerificationDTO {
  verificationType: VerificationType
  sendVerificationEmail: boolean
  periodicalVerificationType: PeriodicalVerificationType
  firstPeriodicalVerification: string
  exceptionTypeDescription: string

  applyInvoiceVerificationRule: boolean

  applyInvoiceAmountThreshold: boolean
  verificationThreshold: number
  verificationThresholdPln: number

  applyUnusualInvoiceAmount: boolean
  amountThresholdFirstNew: number
  amountThresholdFirstNewPln: number

  applyFirstDebtorInvoice: boolean
  varianceThreshold: number

  applyRandomVerification: boolean
  randomNumber: number
}

export interface RiskOfferDTO {
  advanceRate: number
  clientFinancingLimit: number
  exceptionFromHeadOfRisk: boolean
  factoringProduct: FactoringProduct
  fundingLine: FundingLine
  insuredRate: number
  paymentTerm: number
  securityMeasuresApplied: boolean
  withheldAmountRate: number
}

export enum ProspectRiskOfferAgreementType {
  DEBTOR_STATEMENT = 'DEBTOR_STATEMENT',
  INTRODUCTORY_LETTER = 'INTRODUCTORY_LETTER',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}

export interface RiskOfferDebtorDTO {
  debtorStatementRequired: boolean
  debtorStatementType: DebtorStatementType
  companyName: string
  currency: Currency
  id: string
  insuranceType: InsuranceType
  proposedCreditLimit: number
  status: DebtorStatus
}

export interface RiskOfferAuditDTO {
  action: AuditActions
  createdAt: Date
  id: string
  userFullName: string
  clientFinancingLimit: number
}

export interface ProspectRiskOfferDetailsDTO {
  comment: string
  includedDebtors: Array<RiskOfferDebtorDTO>
  invoiceVerification: RiskOfferInvoiceVerificationDTO
  requirementsBeforeFinancing: string
  riskOffer: RiskOfferDTO
  securityMeasure: Array<FactoringSecurityMeasureDTO>
  creditManagementPlan: CreditManagementPlanDTO
  logs: Array<RiskOfferAuditDTO>
}

export interface UpdateProspectRiskOfferDetailsData {
  updateProspectRiskOfferDetails: ProspectRiskOfferDetailsDTO
}

export interface UpdateProspectRiskOfferDetailsFormValues {
  factoringProduct: FactoringProduct
  fundingLine: FundingLine
  insuredRate: number
  clientFinancingLimit: number
  exceptionFromHeadOfRisk: boolean
  advanceRate: number
  paymentTerm: number
  withheldAmountRate: number
  securityMeasuresApplied: boolean
  currency: Currency
}

export enum UpdateProspectRiskOfferDetailsFieldsNames {
  FUNDING_LINE = 'fundingLine',
  FACTORING_PRODUCT = 'factoringProduct',
  EXCEPTION_FROM_HEAD_OH_RISK = 'exceptionFromHeadOfRisk',
  CLIENT_FINANCING_LIMIT = 'clientFinancingLimit',
  ADVANCE_RATE = 'advanceRate',
  INSURED_RATE = 'insuredRate',
  WITHHELD_AMOUNT_RATE = 'withheldAmountRate',
  MAXIMUM_PAYMENT_TERM = 'paymentTerm',
  SECURITY_MEASURES_APPLIED = 'securityMeasuresApplied',
}

export interface UpdateProspectRiskOfferDetailsVariables extends UpdateProspectRiskOfferDetailsFormValues {
  contractId: string
}

export interface UpdateProspectRiskOfferInvoiceVerificationData {
  updateProspectRiskOfferInvoiceVerification: ProspectRiskOfferDetailsDTO
}

export interface UpdateProspectRiskOfferInvoiceVerificationVariables extends InvoiceVerificationFormFields {
  contractId: string
}

export interface ProspectRiskOfferDetailsData {
  loadProspectRiskOfferDetails: ProspectRiskOfferDetailsDTO
}

export interface ProspectRiskOfferDetailsVariables {
  contractId: string
}

export interface UpdateProspectRiskOfferCommentData {
  updateProspectRiskOfferComment: ProspectRiskOfferDetailsDTO
}

export interface UpdateProspectRiskOfferCommentVariables {
  id: string
  summary: string
}

export interface UpdateProspectRiskOfferRequirementsBeforeFinancingData {
  updateProspectRiskOfferRequirementsBeforeFinancing: ProspectRiskOfferDetailsDTO
}

export interface UpdateProspectRiskOfferRequirementsBeforeFinancingVariables {
  id: string
  summary: string
}

export interface ProspectDebtorsForRiskOfferData {
  prospectDebtorsForRiskOffer: Array<DebtorDTO>
}

export interface ProspectDebtorsForRiskOfferVariables {
  contractId: string
}

export interface ProspectContractSecurityMeasuresData {
  listProspectContractSecurityMeasures: Array<FactoringSecurityMeasureDTO>
}

export interface ProspectContractSecurityMeasuresVariables {
  contractId: string
}

export interface AddNewProspectContractSecurityMeasureData {
  addProspectContractSecurityMeasure: FactoringSecurityMeasureDTO
}

export interface AddNewProspectContractSecurityMeasureVariables extends FactoringSecurityMeasureFormValues {
  contractId: string
}

export interface EditProspectContractSecurityMeasureData {
  updateProspectContractSecurityMeasure: FactoringSecurityMeasureDTO
}

export interface EditProspectContractSecurityMeasureVariables extends FactoringSecurityMeasureFormValues {
  id: string
}

export interface RemoveProspectContractSecurityMeasureData {
  deleteProspectContractSecurityMeasure: boolean
}

export interface RemoveProspectContractSecurityMeasureVariables {
  id: string
}

export interface GenerateProspectContractSecurityMeasureData {
  generateProspectContractSecurityMeasure: ClientContractFileDTO
}

export interface GenerateProspectContractSecurityMeasureVariables {
  securityMeasureId: string
}

export interface AssignCmpPlanToRiskOfferData {
  assignCreditManagementPlanToRiskOffer: CreditManagementPlanDTO
}

export interface AssignCmpPlanToRiskOfferVariables extends AssignCmpPlanFormValues {
  contractId: string
}

export enum InsuredCreditManagementPlanType {
  CUSTOM_INSURED = 'CUSTOM_INSURED',
  STANDARD_INSURED = 'STANDARD_INSURED',
}

export interface ProspectContractDebtorRatesDTOInput {
  debtorId: string
  debtor: string
  purchaseRate: number
  paymentTerm: number
  factoringFee?: number
  daysIncluded?: number
  dailyFee?: number
  increasedFee?: number
  monthlyFee?: number
  nonRecourseFee: number

  [key: string]: any
}

export interface GenerateContractDocumentData {
  generateContractDocument: ClientContractFileDTO
}

export interface GenerateContractDocumentFormData {
  additionalInformation: string
  financingTermsVaryByDebtor?: boolean
  debtorRates?: Array<ProspectContractDebtorRatesDTOInput>
  insuranceType?: InsuranceType
  supportingDocuments?: string
}

export interface GenerateContractDocumentVariables extends GenerateContractDocumentFormData {
  contractId: string
}

export enum GenerateContractDocumentFieldsNames {
  ADDITIONAL_INFORMATION = 'additionalInformation',
  INSURANCE_TYPE = 'insuranceType',
  FINANCING_TERMS_VARY_BY_DEBTOR = 'financingTermsVaryByDebtor',
  DEBTOR_RATES = 'debtorRates',
  SUPPORTING_DOCUMENTS = 'supportingDocuments',
}

export enum DebtorRatesFieldArrayNames {
  DEBTOR_NAME = 'debtor',
  DEBTOR_ID = 'debtorId',
  ADVANCED_RATE = 'purchaseRate', //AF IF
  PAYMENT_TERM = 'paymentTerm', //AF IF
  FACTORING_FEE = 'factoringFee', //AF
  FEE_DAYS_INCLUDED = 'daysIncluded', //AF
  DAILY_FEE = 'dailyFee', //AF
  MONTHLY_FEE = 'monthlyFee', // IF
  INCREASED_FEE = 'increasedFee', //IF
  NON_RECOURSE_FEE = 'nonRecourseFee', //AF IF
}

export interface CreateClientProps {
  prospectExistsInFab: boolean
  contractSummary: ProspectContractSummaryDTO
  prospectWarnings: ProspectWarningsDTO
  prospectErrors: ProspectErrors
}

export enum DebtorStatementType {
  ONLY_NOTICE = 'ONLY_NOTICE',
  ONLY_OUTSTANDING = 'ONLY_OUTSTANDING',
  STANDARD = 'STANDARD',
  STANDARD_PLUS_OUTSTANDING = 'STANDARD_PLUS_OUTSTANDING',
  STANDARD_PLUS_SET_OFF = 'STANDARD_PLUS_SET_OFF',
}

export interface DebtorStatementInvoice {
  amount: number | string
  dueDate: string
  invoiceNumber: string
  issueDate: string
  paymentTerm: string

  [field: string]: string | number | boolean | Date | FundingLine | Currency | LoanBookType | undefined
}

export interface ProspectDebtorStatementDTO {
  agreementNumber: string
  attachedDocument: ReportFileDTOInput
  attachedDocumentId: string
  attachedDocumentName: string
  comments: string
  contractId: string
  debtorId: string
  debtorName: string
  debtorStatementNumber: string
  debtorStatementType: DebtorStatementType
  documentLanguage: string
  id: string
  invoices: Array<DebtorStatementInvoice>
  prospectId: string
  setOffRate: number
  startDate: string
  gAmountApplied: boolean
  gAmount: number
  city: string
  country: string
  postalCode: string
  registrationNumber: string
  streetAddressLine1: string
  streetAddressLine2: string
  vatNumber: string
}

export interface Page_ProspectDebtorStatementDTO {
  content: Array<ProspectDebtorStatementDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ProspectDebtorStatementData {
  loadProspectDebtorStatements: Page_ProspectDebtorStatementDTO
}

export interface ProspectDebtorStatementVariables {
  debtorStatementNumber?: string
  debtorStatementType?: Array<DebtorStatementType>
  contractId: string
  debtorName?: string
  agreementNumber?: string
  startDate?: FromToFilterInput
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface ProspectDebtorStatementFormData {
  debtorIds: string[]
  debtorId: string
  debtorStatementType: DebtorStatementType | string
  documentLanguage: string
  startDate: string
  agreementNumber: string
  invoices: Array<DebtorStatementInvoice>
  setOffRate: number | string
  gAmountApplied: boolean
  gAmount: number | string
  attachedDocument?: ReportFileDTOInput
  city?: string
  country?: string
  postalCode?: string
  streetAddressLine1?: string
  streetAddressLine2?: string
}

export enum DebtorStatementInvoicesFieldArrayNames {
  NUMBER = 'invoiceNumber',
  AMOUNT = 'amount',
  ISSUE_DATE = 'issueDate',
  DUE_DATE = 'dueDate',
  PAYMENT_TERM = 'paymentTerm',
}

export enum DebtorStatementFieldNames {
  DEBTORS_IDS = 'debtorIds',
  DEBTORS_ID = 'debtorId',
  TYPE = 'debtorStatementType',
  LANGUAGE = 'documentLanguage',
  START_DATE = 'startDate',
  SET_OFF_RATE = 'setOffRate',
  AGREEMENT_NUMBER = 'agreementNumber',
  INVOICES = 'invoices',
  DOCUMENT = 'attachedDocument',
  G_AMOUNT_APPLIED = 'gAmountApplied',
  G_AMOUNT = 'gAmount',
}

export interface AddProspectDebtorStatementData {
  createProspectDebtorStatement: boolean
}

export interface AddProspectDebtorStatementVariables {
  contractId: string
  documentLanguage: string
  debtorStatementType: DebtorStatementType
  invoices: Array<DebtorStatementInvoice>
  setOffRate: number
  agreementNumber: string
  debtorId: string[] | string
  startDate: string
  gAmountApplied?: boolean
  gAmount?: number
}

export interface UpdateProspectDebtorStatementData {
  updateProspectDebtorStatement: ProspectDebtorStatementDTO
}

export interface UpdateProspectDebtorStatementVariables {
  attachedDocument: ReportFileDTOInput
  documentLanguage: string
  debtorStatementType: DebtorStatementType
  invoices: Array<DebtorStatementInvoice>
  setOffRate: number
  id: string
  agreementNumber: string
  startDate: string
  gAmountApplied?: boolean
  gAmount?: number
}

export interface SendAllDebtorStatementDocumentForSigningData {
  sendAllDebtorStatementDocumentForSigning: boolean
}

export interface SendAllDebtorStatementDocumentForSigningVariables {
  additionalInformation: string
  emailLanguage: string
  contractId: string
  clientEmails: string[]
}

export interface RemoveProspectDebtorStatementData {
  deleteProspectDebtorStatement: boolean
}

export interface RemoveProspectDebtorStatementVariables {
  id: string
}

export interface DebtorInfoDTO {
  id: string
  companyName: string
}

export interface ProspectDebtorStatementDebtorsData {
  loadProspectDebtorStatementDebtors: Array<DebtorInfoDTO>
}

export interface ProspectDebtorStatementDebtorsVariables {
  contractId: string
}

export interface GenerateDebtorStatementData {
  generateProspectDebtorStatement: ProspectReportFileDTO
}

export interface GenerateDebtorStatementVariables {
  documentId: string
}

export interface DownloadAllDebtorStatementsData {
  downloadAllProspectDebtorStatement: ProspectReportFileDTO
}

export interface DownloadAllDebtorStatementsVariables {
  contractId: string
}

export interface RemoveAllProspectDebtorStatementData {
  deleteProspectDebtorStatement: boolean
}

export interface RemoveAllProspectDebtorStatementVariables {
  contractId: string
}

export interface UpdateProspectDebtorDunsVariables {
  debtorId: string
  dunId: string
}

export interface UpdateProspectDebtorDunsData {
  updateProspectDebtorDunsDetails: boolean
}
